import React, { useState, useMemo, useEffect, useRef } from 'react';
import Page from '../components/Page';
import { 
  useDayBook, 
  useActiveShift, 
  useShiftsList, 
  openShift, 
  closeShift, 
  useCurrentRegister, 
  useRegisterByDate 
} from '../controllers/financial.controller';
import { useStoreSettings, usePrintSettings, useCurrencies } from '../controllers/settings.controller';
import { getInvoiceOrders } from '../controllers/invoices.controller';
import { printReceipt } from '../helpers/PrinterHelper';
import { setDetailsForReceiptPrint } from '../helpers/ReceiptHelper';
import FinancialReportsPrintModal from '../components/FinancialReportsPrintModal';
import { getUserDetailsInLocalStorage } from '../helpers/UserDetails';
import { 
  IconEye, 
  IconPrinter, 
  IconFileSpreadsheet, 
  IconFileText, 
  IconRefresh, 
  IconChevronLeft, 
  IconChevronRight, 
  IconCalendar,
  IconSearch,
  IconX,
  IconCheck,
  IconCoins,
  IconArrowUpRight,
  IconArrowDownRight,
  IconBuildingBank,
  IconArrowLeft,
  IconReceipt,
  IconPlayerPlay,
  IconPlayerStop,
  IconCash,
  IconClock,
  IconUser,
  IconCalculator,
  IconAlertTriangle,
  IconPlus,
  IconMinus,
  IconExchange,
  IconCurrencyDollar,
  IconFilter,
  IconBuildingStore,
  IconBed
} from '@tabler/icons-react';
import { toast } from 'react-hot-toast';
import apiClient from '../helpers/ApiClient';

const DEFAULT_CURRENCIES = [
  { code: 'NPR', symbol: 'Rs.', name: 'Nepalese Rupee', is_base: true, exchange_rate: 1.0, denominations: [1000, 500, 100, 50, 20, 10, 5, 2, 1] },
  { code: 'USD', symbol: '$', name: 'US Dollar', is_base: false, exchange_rate: 135.0, denominations: [100, 50, 20, 10, 5, 2, 1] },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', is_base: false, exchange_rate: 1.6, denominations: [500, 200, 100, 50, 20, 10, 5, 2, 1] }
];

const safeText = (val, fallback = '') => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') {
    return val.name || val.title || val.status || val.label || val.table_title || val.room_name || fallback;
  }
  return String(val);
};

class ModalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("[ModalErrorBoundary Caught]:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-[10000] bg-black/65 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-6 max-w-md w-full shadow-2xl border border-red-200 dark:border-red-900/50 text-center animate-in fade-in">
            <div className="w-12 h-12 rounded-2xl bg-red-100 dark:bg-red-950/60 text-red-600 flex items-center justify-center mx-auto mb-3">
              <IconAlertTriangle size={24} />
            </div>
            <h3 className="text-base font-bold text-zinc-900 dark:text-white mb-1">
              Transaction Breakdown Preview
            </h3>
            <p className="text-xs text-zinc-500 mb-4">
              {this.state.error?.message || "Could not format transaction details view."}
            </p>
            <button
              type="button"
              onClick={this.props.onClose}
              className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-zinc-800 hover:bg-zinc-700 transition"
            >
              Close
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function FinancialDayBookPage() {
  // Navigation tabs: 'daily' | 'range' | 'shifts' | 'registers' | 'transactions'
  const [activeTab, setActiveTab] = useState('daily');
  
  // Date states for Day Book
  const [filterType, setFilterType] = useState('today'); // 'today' | 'yesterday' | 'custom' | 'range'
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // Transaction view & drilldown states
  const [drillModal, setDrillModal] = useState({ isOpen: false, title: '', type: '', items: [] });
  const [drillSearch, setDrillSearch] = useState('');
  const [txFilter, setTxFilter] = useState('all');
  const [txFilterTitle, setTxFilterTitle] = useState('All Financial Transactions');
  const [txSearch, setTxSearch] = useState('');

  // Invoice detailed breakdown modal
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);

  // Opening balance edit modal
  const [isOpeningModalOpen, setIsOpeningModalOpen] = useState(false);
  const [newOpeningBalance, setNewOpeningBalance] = useState('');
  const [isSavingOB, setIsSavingOB] = useState(false);

  // Professional Printable Financial Report Modal State
  const [printModal, setPrintModal] = useState({
    isOpen: false,
    reportType: 'sales', // 'sales' | 'purchase' | 'expenses' | 'register' | 'shift_open' | 'shift_close' | 'shift_full'
    selectedShift: null,
    selectedRegister: null,
    dateRange: { from: fromDate, to: toDate }
  });
  const [isPrintDropdownOpen, setIsPrintDropdownOpen] = useState(false);
  const printDropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (printDropdownRef.current && !printDropdownRef.current.contains(event.target)) {
        setIsPrintDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openPrintReport = (reportType = 'sales', customShift = null, customRegister = null) => {
    setIsPrintDropdownOpen(false);
    setPrintModal({
      isOpen: true,
      reportType,
      selectedShift: customShift || activeShift,
      selectedRegister: customRegister || registerData,
      dateRange: { from: fromDate, to: toDate }
    });
  };

  // Queries
  const { data, isLoading, isError, mutate } = useDayBook(fromDate, toDate);
  const { shift: activeShift, summary: shiftSummary, isLoading: isLoadingShift, mutate: mutateShift } = useActiveShift();
  const { shifts: closedShifts, isLoading: isLoadingClosedShifts, mutate: mutateClosedShifts } = useShiftsList();
  const { data: storeSettings } = useStoreSettings();
  const { data: printSettings } = usePrintSettings();
  const { currencies: fetchedCurrencies } = useCurrencies();

  // Register state
  const [selectedRegisterDate, setSelectedRegisterDate] = useState(() => fromDate);
  const { data: currentRegisterData, isLoading: isLoadingCurrentReg, mutate: mutateCurrentReg } = useCurrentRegister();
  const { data: byDateRegisterData, isLoading: isLoadingByDateReg, mutate: mutateByDateReg } = useRegisterByDate(selectedRegisterDate);
  const registerData = selectedRegisterDate === fromDate && currentRegisterData ? currentRegisterData : byDateRegisterData;

  // Shift modals state
  const [isStartShiftModalOpen, setIsStartShiftModalOpen] = useState(false);
  const [isCloseShiftModalOpen, setIsCloseShiftModalOpen] = useState(false);
  const [isStartingShift, setIsStartingShift] = useState(false);
  const [isClosingShift, setIsClosingShift] = useState(false);
  const [shiftStartFloat, setShiftStartFloat] = useState('');
  const [shiftStartNote, setShiftStartNote] = useState('');
  const [shiftCloseCash, setShiftCloseCash] = useState('');
  const [shiftCloseNote, setShiftCloseNote] = useState('');
  const [selectedShiftForDetails, setSelectedShiftForDetails] = useState(null);

  // Tab mode for modals (Quick entry vs Denomination counting)
  const [startShiftTab, setStartShiftTab] = useState('quick'); // 'quick' | 'denominations'
  const [closeShiftTab, setCloseShiftTab] = useState('quick'); // 'quick' | 'denominations'
  const [openingModalTab, setOpeningModalTab] = useState('quick'); // 'quick' | 'denominations'

  // Certified Staff Profile Name
  const userDetails = getUserDetailsInLocalStorage();
  const staffProfileName = userDetails?.name || userDetails?.username || 'Cashier';

  // Multi-Currency & Denominations Drawer state
  const [isDenomDrawerOpen, setIsDenomDrawerOpen] = useState(false);
  const [denomTarget, setDenomTarget] = useState('general'); // 'general' | 'start_shift' | 'close_shift' | 'opening_balance'
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState('');
  const [customRates, setCustomRates] = useState({});
  const [denominationCounts, setDenominationCounts] = useState({});

  const store = storeSettings || data?.storeInfo || {};
  const storeName = store?.store_name || store?.name || "Restaurant";
  const storeAddress = store?.address || store?.store_address || "";
  const storePhone = store?.phone || store?.store_phone || "";
  const storePan = store?.pan_no || store?.tax_number || store?.pan || "";
  const currency = store?.currency || "NPR";

  // Build available currencies with clean denominations
  const availableCurrencies = useMemo(() => {
    if (fetchedCurrencies && fetchedCurrencies.length > 0) {
      return fetchedCurrencies.map(c => {
        let denoms = Array.isArray(c.denominations) ? c.denominations : [];
        if (typeof c.denominations === 'string') {
          try { denoms = JSON.parse(c.denominations); } catch { denoms = []; }
        }
        if (!denoms || denoms.length === 0) {
          if (c.code === 'USD') denoms = [100, 50, 20, 10, 5, 2, 1];
          else if (c.code === 'INR') denoms = [500, 200, 100, 50, 20, 10, 5, 2, 1];
          else if (c.code === 'EUR') denoms = [500, 200, 100, 50, 20, 10, 5, 2, 1];
          else if (c.code === 'GBP') denoms = [50, 20, 10, 5, 2, 1];
          else denoms = [1000, 500, 100, 50, 20, 10, 5, 2, 1];
        }
        return {
          ...c,
          denominations: [...denoms].sort((a, b) => Number(b) - Number(a))
        };
      });
    }
    return DEFAULT_CURRENCIES;
  }, [fetchedCurrencies]);

  // Active currency in denomination calculator
  const activeCurrency = useMemo(() => {
    if (!availableCurrencies || availableCurrencies.length === 0) return DEFAULT_CURRENCIES[0];
    const found = availableCurrencies.find(c => c.code === selectedCurrencyCode);
    if (found) return found;
    const base = availableCurrencies.find(c => Boolean(c.is_base)) || availableCurrencies[0];
    return base;
  }, [availableCurrencies, selectedCurrencyCode]);

  const activeExchangeRate = customRates[activeCurrency.code] !== undefined 
    ? customRates[activeCurrency.code] 
    : Number(activeCurrency.exchange_rate || 1.0);

  // Compute denomination totals across all currencies
  const denominationCalculations = useMemo(() => {
    let grandBaseTotal = 0;
    const perCurrency = {};

    availableCurrencies.forEach(curr => {
      const counts = denominationCounts[curr.code] || {};
      const rate = customRates[curr.code] !== undefined ? customRates[curr.code] : Number(curr.exchange_rate || 1.0);
      let currTotal = 0;
      (curr.denominations || []).forEach(d => {
        const count = Number(counts[d] || 0);
        currTotal += Number(d) * count;
      });
      const baseTotal = currTotal * rate;
      perCurrency[curr.code] = {
        currTotal,
        baseTotal,
        rate,
        counts
      };
      grandBaseTotal += baseTotal;
    });

    return { grandBaseTotal, perCurrency };
  }, [availableCurrencies, denominationCounts, customRates]);

  const handleUpdateDenomCount = (currCode, denom, count) => {
    const val = count === '' ? '' : Math.max(0, parseInt(count, 10) || 0);
    setDenominationCounts(prev => ({
      ...prev,
      [currCode]: {
        ...(prev[currCode] || {}),
        [denom]: val
      }
    }));
  };

  const handleIncrementDenomCount = (currCode, denom, step) => {
    const current = Number(denominationCounts[currCode]?.[denom] || 0);
    handleUpdateDenomCount(currCode, denom, Math.max(0, current + step));
  };

  const handleClearDenominations = (currCode) => {
    if (currCode) {
      setDenominationCounts(prev => ({ ...prev, [currCode]: {} }));
    } else {
      setDenominationCounts({});
    }
  };

  const openDenomDrawerFor = (target) => {
    setDenomTarget(target);
    const base = availableCurrencies.find(c => Boolean(c.is_base)) || availableCurrencies[0];
    setSelectedCurrencyCode(base.code);
    setIsDenomDrawerOpen(true);
  };

  const handleApplyDenominations = () => {
    const total = denominationCalculations.grandBaseTotal;
    if (denomTarget === 'start_shift') {
      setShiftStartFloat(total.toFixed(2));
      toast.success(`Opening Float set to ${currency} ${total.toFixed(2)} from counted denominations`);
    } else if (denomTarget === 'close_shift') {
      setShiftCloseCash(total.toFixed(2));
      toast.success(`Closing Cash set to ${currency} ${total.toFixed(2)} from counted denominations`);
    } else if (denomTarget === 'opening_balance') {
      setNewOpeningBalance(total.toFixed(2));
      toast.success(`Day Book Opening Balance set to ${currency} ${total.toFixed(2)}`);
    } else {
      toast.success(`Counted Total: ${currency} ${total.toFixed(2)}`);
    }
    setIsDenomDrawerOpen(false);
  };

  // Synchronize denomination count into float / closing cash whenever on denominations tab
  useEffect(() => {
    if (isStartShiftModalOpen && startShiftTab === 'denominations') {
      setShiftStartFloat(denominationCalculations.grandBaseTotal.toFixed(2));
    }
  }, [denominationCalculations.grandBaseTotal, isStartShiftModalOpen, startShiftTab]);

  useEffect(() => {
    if (isCloseShiftModalOpen && closeShiftTab === 'denominations') {
      setShiftCloseCash(denominationCalculations.grandBaseTotal.toFixed(2));
    }
  }, [denominationCalculations.grandBaseTotal, isCloseShiftModalOpen, closeShiftTab]);

  useEffect(() => {
    if (isOpeningModalOpen && openingModalTab === 'denominations') {
      setNewOpeningBalance(denominationCalculations.grandBaseTotal.toFixed(2));
    }
  }, [denominationCalculations.grandBaseTotal, isOpeningModalOpen, openingModalTab]);

  // Quick period changer for Day Book
  const handlePeriodPreset = (type) => {
    setFilterType(type);
    const now = new Date();
    if (type === 'today') {
      const d = formatDateStr(now);
      setFromDate(d);
      setToDate(d);
    } else if (type === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const d = formatDateStr(y);
      setFromDate(d);
      setToDate(d);
    } else if (type === 'custom') {
      setToDate(fromDate);
    }
  };

  const handleShiftPeriod = (days) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    from.setDate(from.getDate() + days);
    to.setDate(to.getDate() + days);
    setFromDate(formatDateStr(from));
    setToDate(formatDateStr(to));
  };

  function formatDateStr(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function formatDisplayDate(dateStr) {
    if (!dateStr) return '';
    try {
      const [y, m, d] = dateStr.split('-');
      const date = new Date(y, m - 1, d);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  function parseSafeDate(dateVal) {
    if (!dateVal) return null;
    if (dateVal instanceof Date) return dateVal;
    if (typeof dateVal === 'string') {
      const clean = dateVal.replace(' ', 'T').replace(/Z$/, '').replace(/\.\d+$/, '');
      const d = new Date(clean);
      if (!isNaN(d.getTime())) return d;
      const fallback = new Date(dateVal);
      if (!isNaN(fallback.getTime())) return fallback;
    }
    return null;
  }

  function formatDisplayTime(dateVal, options = { hour: '2-digit', minute: '2-digit' }) {
    const d = parseSafeDate(dateVal);
    if (!d) return '-';
    return d.toLocaleTimeString([], options);
  }

  function formatDisplayDateTime(dateVal, options = { dateStyle: 'short', timeStyle: 'short' }) {
    const d = parseSafeDate(dateVal);
    if (!d) return '-';
    return d.toLocaleString([], options);
  }

  // Calculate financial fields
  const invSummary = data?.invoiceSummary || {};
  const grossSales = Number(invSummary.total_gross || 0);
  const discount = Number(invSummary.total_discount || 0);
  const tax = Number(invSummary.total_tax || 0);
  const roundOff = Number(invSummary.total_round_off || 0);
  const complementary = Number(invSummary.total_complementary || 0);
  const netSales = Number(invSummary.net_sales || 0);
  const totalBilled = grossSales - discount + tax + roundOff;

  // Calculate sales due & due invoice counts for selected period
  const calculatedSalesDue = useMemo(() => {
    const invoices = data?.invoicesList || [];
    return invoices.reduce((acc, i) => {
      const dueAmt = Number(i.due_amount || (Number(i.total || 0) - Number(i.paid_amount || 0)) || 0);
      const status = (i.payment_status || '').toLowerCase();
      if (dueAmt > 0 || status === 'unpaid' || status === 'due' || status === 'partial') {
        const amt = Number(i.due_amount || 0) > 0 ? Number(i.due_amount) : Math.max(0, Number(i.total || 0) - Number(i.paid_amount || 0));
        return acc + amt;
      }
      return acc;
    }, 0);
  }, [data?.invoicesList]);

  const salesDue = Math.max(Number(data?.total_sales_due || 0), calculatedSalesDue);

  const dueInvoicesCount = useMemo(() => {
    const invoices = data?.invoicesList || [];
    return invoices.filter(i => {
      const dueAmt = Number(i.due_amount || (Number(i.total || 0) - Number(i.paid_amount || 0)) || 0);
      const status = (i.payment_status || '').toLowerCase();
      return dueAmt > 0 || status === 'unpaid' || status === 'due' || status === 'partial';
    }).length;
  }, [data?.invoicesList]);

  // Room revenue & room stay charges for selected period
  const totalRoomRevenue = Number(data?.total_room_revenue || 0);
  const roomChargesList = data?.room_charges_list || [];
  const roomChargesCount = roomChargesList.length;

  // Collections breakdown
  const salesPayments = data?.sales_payments || [];
  let calculatedCashCollected = 0;
  let calculatedQrCollected = 0;
  let calculatedCardCollected = 0;
  let calculatedOtherCollected = 0;
  let totalCollected = 0;

  salesPayments.forEach(p => {
    const amt = Number(p.total_amount || 0);
    totalCollected += amt;
    const m = (p.payment_method || '').toLowerCase();
    if (m.includes('cash')) calculatedCashCollected += amt;
    else if (m.includes('qr') || m.includes('fonepay') || m.includes('esewa') || m.includes('khalti') || m.includes('online') || m.includes('upi')) calculatedQrCollected += amt;
    else if (m.includes('card') || m.includes('pos') || m.includes('visa') || m.includes('master')) calculatedCardCollected += amt;
    else calculatedOtherCollected += amt;
  });

  const totalCashCollected = data?.total_cash_collected !== undefined ? Number(data.total_cash_collected) : calculatedCashCollected;
  const totalQrCollected = data?.total_qr_collected !== undefined ? Number(data.total_qr_collected) : calculatedQrCollected;
  const totalCardCollected = data?.total_card_collected !== undefined ? Number(data.total_card_collected) : calculatedCardCollected;
  if (totalCollected === 0 && (totalCashCollected > 0 || totalQrCollected > 0)) {
    totalCollected = totalCashCollected + totalQrCollected + totalCardCollected + Number(data?.total_other_collected || 0);
  }

  // Cash Flow breakdown
  const openingBalance = Number(data?.opening_balance || 0);
  const cashSales = totalCashCollected;
  const previousDueCollected = Number(data?.due_collected || 0);
  const otherCashReceipts = Number(data?.total_transfers_in || 0) + Number(data?.total_journal_in || 0);
  const totalCashIn = cashSales + previousDueCollected + otherCashReceipts;

  let cashPurchases = 0;
  let cashExpenses = 0;
  salesPayments.forEach(p => {
    const m = (p.payment_method || '').toLowerCase();
    if (m.includes('cash')) {
      cashPurchases += Number(p.purchases_paid || 0);
      cashExpenses += Number(p.expenses_paid || 0);
    }
  });
  if (cashExpenses === 0 && Number(data?.total_expenses || 0) > 0) {
    cashExpenses = Number(data?.total_expenses || 0);
  }
  const cashRefunds = 0;
  const cashWithdrawals = Number(data?.total_transfers_out || 0);
  const otherCashPayments = Number(data?.total_journal_out || 0);
  const totalCashOut = cashPurchases + cashExpenses + cashRefunds + cashWithdrawals + otherCashPayments;

  const expectedClosingCash = openingBalance + totalCashIn - totalCashOut;

  // Navigate to transactions tab
  const navigateToTransactions = (title, type) => {
    setTxFilter(type || 'all');
    setTxFilterTitle(title || 'Transactions');
    setTxSearch('');
    setActiveTab('transactions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Open drill-down modal & synchronize transactions tab
  const openDrillDown = (title, type) => {
    const filterKey = type || 'all';
    setTxFilter(filterKey);
    setTxFilterTitle(title || 'Transactions');
    setTxSearch('');
    setDrillSearch('');
    setDrillModal({
      isOpen: true,
      title: title || 'Filtered Transactions',
      type: filterKey
    });
  };

  // Filtered transactions for the Transaction Reports view
  const filteredTransactions = useMemo(() => {
    const invoices = data?.invoicesList || [];
    const compItems = data?.complementaryList || [];
    let list = [];

    if (txFilter === 'gross' || txFilter === 'billed' || txFilter === 'all') {
      list = invoices;
    } else if (txFilter === 'net') {
      list = invoices;
    } else if (txFilter === 'discount') {
      list = invoices.filter(i => Number(i.discount || 0) > 0);
    } else if (txFilter === 'tax') {
      list = invoices.filter(i => Number(i.tax_total || 0) > 0);
    } else if (txFilter === 'round_off') {
      list = invoices.filter(i => Number(i.round_off || 0) !== 0);
    } else if (txFilter === 'complementary') {
      list = compItems.map(c => ({
        id: c.order_id || c.id,
        invoice_no: `COMP-${c.token_no || c.id}`,
        token_no: c.token_no,
        created_at: c.date,
        customer_name: 'Complementary Order',
        payment_method_name: 'Complimentary',
        sub_total: c.total,
        total: c.total,
        discount: 0,
        tax_total: 0,
        round_off: 0,
        due_amount: 0,
        payment_status: 'complimentary',
        item_name: c.item_name,
        price: c.price,
        quantity: c.quantity,
        is_complementary: true
      }));
    } else if (txFilter === 'due') {
      list = invoices.filter(i => {
        const dueAmt = Number(i.due_amount || (Number(i.total || 0) - Number(i.paid_amount || 0)) || 0);
        const status = (i.payment_status || '').toLowerCase();
        return dueAmt > 0 || status === 'unpaid' || status === 'due' || status === 'partial';
      }).map(i => {
        const calculatedDue = Number(i.due_amount || 0) > 0 ? Number(i.due_amount) : Math.max(0, Number(i.total || 0) - Number(i.paid_amount || 0));
        return {
          ...i,
          due_amount: calculatedDue
        };
      });
    } else if (txFilter === 'due_collected') {
      const dcList = data?.due_collected_list || [];
      list = dcList.map(dc => ({
        id: dc.id,
        invoice_no: dc.invoice_no || `REC-${dc.id}`,
        token_no: dc.invoice_no,
        created_at: dc.payment_date || dc.created_at,
        customer_name: dc.customer_name || 'Customer Due Collection',
        payment_method_name: dc.payment_method || 'Cash',
        sub_total: dc.amount,
        total: dc.amount,
        paid_amount: dc.amount,
        due_amount: 0,
        payment_status: 'collected',
        is_due_collection: true
      }));
    } else if (txFilter === 'room_revenue') {
      const rList = data?.room_charges_list || [];
      list = rList.map(r => ({
        id: r.id,
        invoice_no: r.invoice_no || `ROOM-${r.room_number || r.id}`,
        token_no: r.room_number,
        created_at: r.created_at || r.date,
        customer_name: r.customer_name || `Room ${r.room_number} Guest`,
        customer_phone: r.customer_phone || '',
        payment_method_name: r.payment_method_name || 'Room Charge',
        sub_total: r.sub_total || r.total,
        total: r.total,
        tax_total: 0,
        discount: 0,
        paid_amount: r.paid_amount || r.total,
        due_amount: r.due_amount || 0,
        payment_status: r.payment_status || 'Paid',
        item_name: r.item_name || `Room ${r.room_number} Stay (${r.nights || 1} Night${(r.nights || 1) === 1 ? '' : 's'})`,
        price: r.room_rate || r.total,
        quantity: r.nights || 1,
        is_room_charge: true
      }));
    } else if (txFilter.startsWith('payment_')) {
      const method = txFilter.replace('payment_', '').toLowerCase();
      list = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes(method));
    } else if (txFilter === 'cash') {
      list = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes('cash'));
    } else if (txFilter === 'card') {
      list = invoices.filter(i => (i.payment_method_name || '').toLowerCase().includes('card'));
    } else if (txFilter === 'online' || txFilter === 'qr') {
      list = invoices.filter(i => {
        const m = (i.payment_method_name || '').toLowerCase();
        return m.includes('qr') || m.includes('fonepay') || m.includes('online') || m.includes('esewa') || m.includes('khalti');
      });
    } else {
      list = invoices;
    }

    if (!txSearch) return list;
    const q = txSearch.toLowerCase();
    return list.filter(item => {
      return (
        (item.invoice_no && item.invoice_no.toLowerCase().includes(q)) ||
        (item.token_no && String(item.token_no).includes(q)) ||
        (item.customer_name && item.customer_name.toLowerCase().includes(q)) ||
        (item.payment_method_name && item.payment_method_name.toLowerCase().includes(q)) ||
        (item.item_name && item.item_name.toLowerCase().includes(q)) ||
        (item.id && String(item.id).includes(q))
      );
    });
  }, [data?.invoicesList, data?.complementaryList, data?.due_collected_list, data?.room_charges_list, txFilter, txSearch]);

  const filteredTransactionsTotal = useMemo(() => {
    return filteredTransactions.reduce((acc, item) => {
      if (txFilter === 'due') {
        return acc + Number(item.due_amount || 0);
      }
      if (txFilter === 'discount') {
        return acc + Number(item.discount || 0);
      }
      if (txFilter === 'tax') {
        return acc + Number(item.tax_total || 0);
      }
      if (txFilter === 'round_off') {
        return acc + Math.abs(Number(item.round_off || 0));
      }
      return acc + Number(item.total || (item.price * item.quantity) || item.amount || 0);
    }, 0);
  }, [filteredTransactions, txFilter]);

  // Modal drilldown items computed from drillModal.type and drillSearch
  const modalDrillItems = useMemo(() => {
    if (!drillModal.isOpen) return [];
    const invoices = data?.invoicesList || [];
    const compItems = data?.complementaryList || [];
    let list = [];
    const t = drillModal.type;

    if (t === 'gross' || t === 'billed' || t === 'all') {
      list = invoices;
    } else if (t === 'net') {
      list = invoices;
    } else if (t === 'discount') {
      list = invoices.filter(i => Number(i.discount || 0) > 0);
    } else if (t === 'tax') {
      list = invoices.filter(i => Number(i.tax_total || 0) > 0);
    } else if (t === 'round_off') {
      list = invoices.filter(i => Number(i.round_off || 0) !== 0);
    } else if (t === 'complementary') {
      list = compItems.map(c => ({
        id: c.order_id || c.id,
        invoice_no: `COMP-${c.token_no || c.id}`,
        token_no: c.token_no,
        created_at: c.date,
        customer_name: 'Complementary Order',
        payment_method_name: 'Complimentary',
        sub_total: c.total,
        total: c.total,
        discount: 0,
        tax_total: 0,
        round_off: 0,
        due_amount: 0,
        payment_status: 'complimentary',
        item_name: c.item_name,
        price: c.price,
        quantity: c.quantity,
        is_complementary: true
      }));
    } else if (t === 'due') {
      list = invoices.filter(i => {
        const dueAmt = Number(i.due_amount || (Number(i.total || 0) - Number(i.paid_amount || 0)) || 0);
        const status = (i.payment_status || '').toLowerCase();
        return dueAmt > 0 || status === 'unpaid' || status === 'due' || status === 'partial';
      }).map(i => {
        const calculatedDue = Number(i.due_amount || 0) > 0 ? Number(i.due_amount) : Math.max(0, Number(i.total || 0) - Number(i.paid_amount || 0));
        return {
          ...i,
          due_amount: calculatedDue
        };
      });
    } else if (t === 'due_collected') {
      const dcList = data?.due_collected_list || [];
      list = dcList.map(dc => ({
        id: dc.id,
        invoice_no: dc.invoice_no || `REC-${dc.id}`,
        token_no: dc.invoice_no,
        created_at: dc.payment_date || dc.created_at,
        customer_name: dc.customer_name || 'Customer Due Collection',
        payment_method_name: dc.payment_method || 'Cash',
        sub_total: dc.amount,
        total: dc.amount,
        paid_amount: dc.amount,
        due_amount: 0,
        payment_status: 'collected',
        is_due_collection: true
      }));
    } else if (t === 'room_revenue') {
      const rList = data?.room_charges_list || [];
      list = rList.map(r => ({
        id: r.id,
        invoice_no: r.invoice_no || `ROOM-${r.room_number || r.id}`,
        token_no: r.room_number,
        created_at: r.created_at || r.date,
        customer_name: r.customer_name || `Room ${r.room_number} Guest`,
        customer_phone: r.customer_phone || '',
        payment_method_name: r.payment_method_name || 'Room Charge',
        sub_total: r.sub_total || r.total,
        total: r.total,
        tax_total: 0,
        discount: 0,
        paid_amount: r.paid_amount || r.total,
        due_amount: r.due_amount || 0,
        payment_status: r.payment_status || 'Paid',
        item_name: r.item_name || `Room ${r.room_number} Stay (${r.nights || 1} Night${(r.nights || 1) === 1 ? '' : 's'})`,
        price: r.room_rate || r.total,
        quantity: r.nights || 1,
        is_room_charge: true
      }));
    } else if (t.startsWith('payment_')) {
      const method = t.replace('payment_', '').toLowerCase();
      list = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes(method));
    } else if (t === 'cash') {
      list = invoices.filter(i => (i.payment_method_name || 'Cash').toLowerCase().includes('cash'));
    } else if (t === 'card') {
      list = invoices.filter(i => (i.payment_method_name || '').toLowerCase().includes('card'));
    } else if (t === 'online' || t === 'qr') {
      list = invoices.filter(i => {
        const m = (i.payment_method_name || '').toLowerCase();
        return m.includes('qr') || m.includes('fonepay') || m.includes('online') || m.includes('esewa') || m.includes('khalti');
      });
    } else if (t === 'purchases' || t === 'cash_purchases') {
      const purchases = data?.purchases || [];
      list = (t === 'cash_purchases'
        ? purchases.filter(p => !p.payment_method || p.payment_method.toLowerCase().includes('cash'))
        : purchases
      ).map(p => ({
        id: p.id,
        invoice_no: p.purchase_invoice_no || `PUR-${p.id}`,
        created_at: p.purchase_date,
        customer_name: p.supplier_name || 'Supplier Purchase',
        payment_method_name: p.payment_method || 'Cash',
        sub_total: p.total_amount,
        total: p.total_amount,
        discount: 0,
        tax_total: 0,
        paid_amount: p.paid_amount,
        due_amount: p.due_amount,
        payment_status: p.payment_status || (Number(p.due_amount) > 0 ? 'due' : 'paid'),
        is_purchase: true
      }));
    } else if (t === 'expenses' || t === 'cash_expenses') {
      const expenses = data?.expenses || [];
      list = (t === 'cash_expenses'
        ? expenses.filter(e => !e.payment_method || e.payment_method.toLowerCase().includes('cash'))
        : expenses
      ).map(e => ({
        id: e.id,
        invoice_no: `EXP-${e.id}`,
        created_at: e.date,
        customer_name: e.supplier_name || e.category_name || 'Expense Voucher',
        payment_method_name: e.payment_method || 'Cash',
        sub_total: e.amount,
        total: e.amount,
        discount: 0,
        tax_total: 0,
        paid_amount: e.paid_amount || e.amount,
        due_amount: e.due_amount || 0,
        payment_status: e.payment_status || (Number(e.due_amount) > 0 ? 'due' : 'paid'),
        item_name: e.description || e.category_name,
        is_expense: true
      }));
    } else {
      list = invoices;
    }

    if (!drillSearch) return list;
    const q = drillSearch.toLowerCase();
    return list.filter(item => {
      return (
        (item.invoice_no && item.invoice_no.toLowerCase().includes(q)) ||
        (item.token_no && String(item.token_no).includes(q)) ||
        (item.customer_name && item.customer_name.toLowerCase().includes(q)) ||
        (item.payment_method_name && item.payment_method_name.toLowerCase().includes(q)) ||
        (item.item_name && item.item_name.toLowerCase().includes(q)) ||
        (item.id && String(item.id).includes(q))
      );
    });
  }, [drillModal.isOpen, drillModal.type, drillSearch, data?.invoicesList, data?.complementaryList, data?.purchases, data?.expenses, data?.due_collected_list, data?.room_charges_list]);

  const modalDrillTotal = useMemo(() => {
    return modalDrillItems.reduce((acc, item) => {
      if (drillModal.type === 'due') {
        return acc + Number(item.due_amount || 0);
      }
      if (drillModal.type === 'discount') {
        return acc + Number(item.discount || 0);
      }
      if (drillModal.type === 'tax') {
        return acc + Number(item.tax_total || 0);
      }
      if (drillModal.type === 'round_off') {
        return acc + Math.abs(Number(item.round_off || 0));
      }
      return acc + Number(item.total || (item.price * item.quantity) || item.amount || 0);
    }, 0);
  }, [modalDrillItems, drillModal.type]);

  // Open invoice detailed breakdown modal
  const handleViewInvoice = async (invoice) => {
    if (!invoice) return;
    try {
      setIsLoadingInvoice(true);
      const toastId = toast.loading("Loading transaction details...");
      const invoiceId = invoice.id || invoice.invoice_id || invoice.raw_id || 'N/A';
      let orderData = null;

      // Only attempt orders API for actual sales invoices or general transactions
      const isCustomType = Boolean(invoice.is_purchase || invoice.is_expense || invoice.is_due_collection || invoice.is_room_charge || invoice.is_complementary);

      if (!isCustomType && invoiceId && invoiceId !== 'N/A') {
        try {
          const res = await getInvoiceOrders(invoiceId);
          if (res && res.status === 200 && res.data) {
            orderData = res.data;
          }
        } catch (err) {
          console.warn("[handleViewInvoice] Could not fetch detailed orders:", err?.message);
        }
      }

      if (toastId) toast.dismiss(toastId);
      else toast.dismiss();

      // Normalize orders list from either array response or object response
      let normalizedOrders = [];
      if (Array.isArray(orderData)) {
        normalizedOrders = orderData;
      } else if (orderData && Array.isArray(orderData.orders)) {
        normalizedOrders = orderData.orders;
      } else if (invoice.orders && Array.isArray(invoice.orders)) {
        normalizedOrders = invoice.orders;
      }

      // Deep normalize items and addons
      const cleanOrders = normalizedOrders.map((order, oIdx) => {
        if (!order) return { id: oIdx, items: [] };
        let items = order.items;
        if (typeof items === 'string') {
          try { items = JSON.parse(items); } catch(e) { items = []; }
        }
        if (!Array.isArray(items)) items = [];

        const cleanItems = items.map((it, itIdx) => {
          if (!it) return { id: itIdx, item_title: 'Item', quantity: 1, price: 0, addons: [] };
          let addons = it.addons;
          if (typeof addons === 'string' && addons.trim()) {
            try { 
              const parsed = JSON.parse(addons);
              addons = Array.isArray(parsed) ? parsed : [parsed];
            } catch(e) { 
              addons = addons.split(',').map(s => ({ title: s.trim() })); 
            }
          }
          if (!Array.isArray(addons)) addons = [];

          return {
            ...it,
            item_title: safeText(it.item_title || it.title, 'Menu Item'),
            variant_title: it.variant_title ? safeText(it.variant_title, '') : null,
            notes: it.notes ? safeText(it.notes, '') : '',
            quantity: Number(it.quantity || 1) || 1,
            price: Number(it.price !== undefined ? it.price : (it.variant_price !== undefined ? it.variant_price : 0)) || 0,
            addons
          };
        });

        return {
          ...order,
          table_title: safeText(order.table_title, ''),
          room_name: safeText(order.room_name, ''),
          customer_name: safeText(order.customer_name, ''),
          items: cleanItems
        };
      });

      // Synthesize item breakdown if no nested order items were returned
      if (cleanOrders.length === 0 || cleanOrders.every(o => !o.items || o.items.length === 0)) {
        let fallbackTitle = 'Order Bill';
        let fallbackQty = Number(invoice.quantity || 1) || 1;
        let fallbackPrice = Number(invoice.sub_total || invoice.total || invoice.amount || 0) || 0;

        if (invoice.is_purchase) {
          fallbackTitle = safeText(invoice.customer_name ? `Purchase: ${invoice.customer_name}` : 'Supplier Purchase Voucher');
          fallbackPrice = Number(invoice.total || invoice.sub_total || 0) || 0;
        } else if (invoice.is_expense) {
          fallbackTitle = safeText(invoice.customer_name ? `Expense: ${invoice.customer_name}` : 'Operating Expense Voucher');
          fallbackPrice = Number(invoice.total || invoice.amount || 0) || 0;
        } else if (invoice.is_due_collection) {
          fallbackTitle = safeText(invoice.customer_name ? `Due Collected: ${invoice.customer_name}` : 'Customer Due Collection');
          fallbackPrice = Number(invoice.total || invoice.paid_amount || 0) || 0;
        } else if (invoice.is_room_charge) {
          fallbackTitle = safeText(invoice.item_name || `Room Stay: ${invoice.room_number || invoice.token_no || ''}`);
          fallbackQty = Number(invoice.quantity || 1) || 1;
          fallbackPrice = Number(invoice.price || invoice.total || 0) || 0;
        } else if (invoice.is_complementary) {
          fallbackTitle = safeText(invoice.item_name || 'Complimentary Item');
          fallbackQty = Number(invoice.quantity || 1) || 1;
          fallbackPrice = Number(invoice.price || 0) || 0;
        } else if (invoice.item_name) {
          fallbackTitle = safeText(invoice.item_name);
          fallbackQty = Number(invoice.quantity || 1) || 1;
          fallbackPrice = Number(invoice.price || invoice.total || 0) || 0;
        } else {
          fallbackTitle = `Invoice #${safeText(invoice.invoice_no || invoiceId)}`;
          fallbackPrice = Number(invoice.sub_total !== undefined ? invoice.sub_total : (invoice.total || invoice.amount || 0)) || 0;
        }

        cleanOrders.push({
          id: `fallback-${invoiceId}`,
          table_title: safeText(invoice.table_title || invoice.room_name, ''),
          customer_name: safeText(invoice.customer_name, 'Walk-in Customer'),
          items: [{
            id: `fb-item-${invoiceId}`,
            item_title: fallbackTitle,
            quantity: fallbackQty,
            price: fallbackPrice,
            variant_title: null,
            notes: safeText(invoice.notes || ''),
            addons: []
          }]
        });
      }

      const orderObj = (typeof orderData === 'object' && !Array.isArray(orderData) && orderData !== null) ? orderData : {};

      const resolvedInvNo = safeText(invoice.invoice_no || orderObj.invoice_no || (invoiceId !== 'N/A' ? `INV-${invoiceId}` : 'INV'));
      const resolvedCustomer = safeText(invoice.customer_name || orderObj.customer_name || invoice.name, 'Walk-in Customer');
      const resolvedPaymentMethod = safeText(invoice.payment_method_name || invoice.payment_method || orderObj.payment_method_name, 'Cash');
      const resolvedPaymentStatus = safeText(invoice.payment_status || orderObj.payment_status, 'completed');
      const resolvedTableOrRoom = safeText(invoice.table_title || invoice.room_name || orderObj.table_title || (cleanOrders[0]?.table_title) || (invoice.room_number ? `Room ${invoice.room_number}` : 'N/A'));

      const safeTotal = Number(invoice.total !== undefined ? invoice.total : (orderObj.total !== undefined ? orderObj.total : (invoice.amount || 0))) || 0;
      const safeSubtotal = Number(invoice.sub_total !== undefined ? invoice.sub_total : (orderObj.subtotal !== undefined ? orderObj.subtotal : (invoice.subtotal !== undefined ? invoice.subtotal : safeTotal))) || 0;
      const safeDiscount = Number(invoice.discount !== undefined ? invoice.discount : (orderObj.discount || 0)) || 0;
      const safeTax = Number(invoice.tax_total !== undefined ? invoice.tax_total : (orderObj.taxTotal || (invoice.taxTotal || 0))) || 0;
      const safeRoundOff = Number(invoice.round_off !== undefined ? invoice.round_off : (orderObj.roundOff || (invoice.roundOff || 0))) || 0;
      const safePaid = Number(invoice.paid_amount !== undefined ? invoice.paid_amount : (orderObj.paidAmount !== undefined ? orderObj.paidAmount : (invoice.paidAmount !== undefined ? invoice.paidAmount : safeTotal))) || 0;
      const safeDue = Number(invoice.due_amount !== undefined ? invoice.due_amount : (orderObj.dueAmount !== undefined ? orderObj.dueAmount : (invoice.dueAmount || 0))) || 0;

      setViewingInvoice({
        ...invoice,
        ...orderObj,
        orders: cleanOrders,
        invoiceId: invoiceId,
        invoiceNo: resolvedInvNo,
        customer_name: resolvedCustomer,
        payment_method_name: resolvedPaymentMethod,
        payment_status: resolvedPaymentStatus,
        table_title: resolvedTableOrRoom,
        total: safeTotal,
        subtotal: safeSubtotal,
        discount: safeDiscount,
        taxTotal: safeTax,
        roundOff: safeRoundOff,
        paidAmount: safePaid,
        dueAmount: safeDue,
        staff_name: safeText(invoice.staff_name || orderObj.staff_name || '')
      });
    } catch (err) {
      if (typeof toast !== 'undefined') toast.dismiss();
      console.error("[handleViewInvoice] Error viewing invoice:", err);
      const safeId = safeText(invoice?.id || 'N/A');
      setViewingInvoice({
        ...invoice,
        orders: [{
          id: `err-${safeId}`,
          items: [{
            id: `err-it-${safeId}`,
            item_title: safeText(invoice?.invoice_no || `Invoice #${safeId}`),
            quantity: 1,
            price: Number(invoice?.total || invoice?.amount || 0) || 0,
            addons: []
          }]
        }],
        invoiceId: safeId,
        invoiceNo: safeText(invoice?.invoice_no || `INV-${safeId}`),
        customer_name: safeText(invoice?.customer_name, 'Walk-in Customer'),
        payment_method_name: safeText(invoice?.payment_method_name || invoice?.payment_method, 'Cash'),
        payment_status: safeText(invoice?.payment_status, 'completed'),
        total: Number(invoice?.total || invoice?.amount || 0) || 0,
        subtotal: Number(invoice?.sub_total || invoice?.total || 0) || 0,
        paidAmount: Number(invoice?.paid_amount || invoice?.total || 0) || 0,
        dueAmount: Number(invoice?.due_amount || 0) || 0
      });
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  // Print thermal receipt for single transaction
  const handlePrintTransaction = async (invoice) => {
    try {
      const toastId = toast.loading("Preparing receipt...");
      const invoiceId = invoice.id || invoice.invoice_id || invoice.raw_id;
      let orderData = null;
      try {
        if (invoiceId) {
          const res = await getInvoiceOrders(invoiceId);
          if (res && res.status === 200) {
            orderData = res.data;
          }
        }
      } catch (err) {
        console.warn("Could not fetch detailed orders, using invoice record:", err);
      }
      if (toastId) toast.dismiss(toastId);
      else toast.dismiss();

      let ordersArr = [];
      if (Array.isArray(orderData)) ordersArr = orderData;
      else if (orderData && Array.isArray(orderData.orders)) ordersArr = orderData.orders;
      else if (invoice.orders && Array.isArray(invoice.orders)) ordersArr = invoice.orders;

      let cartItems = [];
      for (const o of ordersArr) {
        if (!o) continue;
        let items = o.items;
        if (typeof items === 'string') {
          try { items = JSON.parse(items); } catch(e) { items = []; }
        }
        if (!Array.isArray(items)) items = [];

        items.forEach(i => {
          if (!i) return;
          let addons = i?.addons;
          if (typeof addons === 'string' && addons.trim()) {
            try { addons = JSON.parse(addons); } catch(e) { addons = []; }
          }
          if (!Array.isArray(addons)) addons = [];

          cartItems.push({
            ...i,
            title: safeText(i.item_title || i.title, "Item"),
            addons_ids: addons.map(a => (typeof a === 'object' && a !== null ? (a.id || a.addon_id) : a)).filter(Boolean),
            variant: i.variant_id ? { id: i.variant_id, title: safeText(i.variant_title), price: Number(i.variant_price || 0) } : null
          });
        });
      }

      if (cartItems.length === 0) {
        cartItems.push({
          title: safeText(invoice.item_name || invoice.invoice_no || `Invoice #${invoiceId}`),
          quantity: Number(invoice.quantity || 1) || 1,
          price: Number(invoice.sub_total || invoice.total || invoice.amount || 0) || 0
        });
      }

      const billFormat = printSettings?.bill_format || localStorage.getItem("bill_format") || "retail_tax_invoice";
      const pageFormat = printSettings?.page_format || localStorage.getItem("page_format") || "58";

      const resolvedTableTitle = safeText(orderData?.table_title || invoice.table_title || (orderData?.orders && orderData.orders[0]?.table_title) || (orderData?.room_name ? `Room ${orderData.room_name}` : null) || (invoice.room_number ? `Room ${invoice.room_number}` : null), 'N/A');
      const resolvedCustomerName = safeText(orderData?.customer_name || invoice.customer_name || invoice.name || (orderData?.orders && orderData.orders[0]?.customer_name), "Walk-in Customer");
      const resolvedCustomerId = safeText(orderData?.customer_id || invoice.customer_id || (orderData?.orders && orderData.orders[0]?.customer_id), "Walk-in");

      const receiptDetails = {
        cartItems,
        customerType: "CUSTOMER",
        customer: {
          id: resolvedCustomerId,
          name: resolvedCustomerName
        },
        tableId: null,
        table_title: resolvedTableTitle,
        currency: currency,
        storeSettings: store,
        printSettings: printSettings || {},
        billFormat,
        pageFormat,
        itemsTotal: Number(orderData?.subtotal !== undefined ? orderData.subtotal : (invoice.sub_total || invoice.total || 0)) || 0,
        taxTotal: Number(orderData?.taxTotal !== undefined ? orderData.taxTotal : (invoice.tax_total || 0)) || 0,
        serviceChargeTotal: Number(orderData?.serviceChargeTotal || 0) || 0,
        payableTotal: Number(orderData?.total !== undefined ? orderData.total : (invoice.total || 0)) || 0,
        storedDiscount: Number(orderData?.discount !== undefined ? orderData.discount : (invoice.discount || 0)) || 0,
        roundOff: Number(orderData?.roundOff !== undefined ? orderData.roundOff : (invoice.round_off || 0)) || 0,
        paidAmount: Number(orderData?.paidAmount !== undefined ? orderData.paidAmount : (invoice.paid_amount !== undefined ? invoice.paid_amount : invoice.total)) || 0,
        dueAmount: Number(orderData?.dueAmount !== undefined ? orderData.dueAmount : (invoice.due_amount || 0)) || 0,
        tokenNo: safeText(invoice.token_no, '-'),
        orderId: String(invoiceId),
        created_at: invoice.created_at || invoice.date || new Date(),
        date: invoice.created_at || invoice.date || new Date(),
        invoiceId: invoiceId,
        invoice_id: invoiceId,
        billNo: safeText(invoice.invoice_no || `INV-${invoiceId}`),
        invoiceNo: safeText(invoice.invoice_no || `INV-${invoiceId}`),
        paymentMethod: safeText(invoice.payment_method_name || invoice.payment_method, "Cash")
      };

      setDetailsForReceiptPrint(receiptDetails);
      await printReceipt(receiptDetails);
      toast.success("Receipt sent to printer!");
    } catch (err) {
      toast.dismiss();
      console.error("Print receipt error:", err);
      toast.error("Failed to print receipt");
    }
  };

  // Print Shift Z-Report / Shift Statement / Shift Open Report
  const handlePrintShift = (shiftItem, type = 'shift_close') => {
    openPrintReport(type, shiftItem);
  };

  // CSV Export
  const handleExportCSV = () => {
    let csv = `Day Book Financial Report - ${storeName}\n`;
    csv += `Period: ${fromDate} to ${toDate}\n\n`;

    csv += `SALES SUMMARY\n`;
    csv += `Item,Amount (${currency})\n`;
    csv += `Gross Sales,${grossSales.toFixed(2)}\n`;
    csv += `Discount,-${discount.toFixed(2)}\n`;
    csv += `Net Sales,${netSales.toFixed(2)}\n`;
    csv += `VAT / Tax,${tax.toFixed(2)}\n`;
    csv += `Round Off,${roundOff.toFixed(2)}\n`;
    csv += `Complementary,${complementary.toFixed(2)}\n`;
    csv += `Total Billed,${totalBilled.toFixed(2)}\n\n`;

    csv += `COLLECTION SUMMARY\n`;
    csv += `Payment Type,Amount (${currency})\n`;
    salesPayments.forEach(p => {
      csv += `${p.payment_method},${Number(p.total_amount || 0).toFixed(2)}\n`;
    });
    csv += `Total Collected,${totalCollected.toFixed(2)}\n`;
    csv += `Sales Due,${salesDue.toFixed(2)}\n\n`;

    csv += `CASH FLOW\n`;
    csv += `Cash Opening,${openingBalance.toFixed(2)}\n`;
    csv += `Cash Sales,${cashSales.toFixed(2)}\n`;
    csv += `Previous Due Collection,${previousDueCollected.toFixed(2)}\n`;
    csv += `Other Cash Receipts,${otherCashReceipts.toFixed(2)}\n`;
    csv += `Cash Purchases,-${cashPurchases.toFixed(2)}\n`;
    csv += `Cash Expenses,-${cashExpenses.toFixed(2)}\n`;
    csv += `Expected Closing Cash,${expectedClosingCash.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DayBook_${fromDate}_to_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Save Opening Balance
  const handleSaveOpeningBalance = async () => {
    try {
      setIsSavingOB(true);
      await apiClient.post('/reports/day-book/opening-balance', {
        date: fromDate,
        amount: parseFloat(newOpeningBalance) || 0,
        denominations: denominationCounts
      });
      toast.success("Opening balance updated successfully!");
      setIsOpeningModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err.message || "Failed to update opening balance");
    } finally {
      setIsSavingOB(false);
    }
  };

  // Start Shift Handler
  const handleStartShiftSubmit = async (e) => {
    if (e) e.preventDefault();
    const floatAmount = parseFloat(shiftStartFloat) || 0;
    if (floatAmount <= 0 && !window.confirm("Start shift with 0.00 opening float?")) {
      return;
    }
    try {
      setIsStartingShift(true);
      await openShift(floatAmount, denominationCounts, shiftStartNote);

      toast.success("Shift started successfully!");
      setIsStartShiftModalOpen(false);
      setShiftStartFloat('');
      setShiftStartNote('');
      mutateShift();
      mutate();
      mutateClosedShifts();
    } catch (err) {
      console.error("Start shift error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to start shift");
    } finally {
      setIsStartingShift(false);
    }
  };

  // Close Shift Calculations
  const activeShiftOpening = Number(activeShift?.opening_balance || activeShift?.opening_cash || activeShift?.starting_float || 0);
  const activeShiftCashSales = Number(activeShift?.system_cash_sales !== undefined ? activeShift.system_cash_sales : (shiftSummary?.payments?.find(p => (p.payment_method || '').toLowerCase() === 'cash')?.total || 0));
  const activeShiftCashExpenses = Number(activeShift?.system_expenses !== undefined ? activeShift.system_expenses : (shiftSummary?.expenses?.find(e => (e.payment_method || '').toLowerCase() === 'cash')?.total || 0));
  const activeShiftExpectedCash = activeShift?.system_expected_cash !== undefined 
    ? Number(activeShift.system_expected_cash) 
    : (activeShiftOpening + activeShiftCashSales - activeShiftCashExpenses);
  const parsedCloseCash = parseFloat(shiftCloseCash) || 0;
  const activeShiftDiff = parsedCloseCash - activeShiftExpectedCash;

  // Close Shift Handler
  const handleCloseShiftSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!activeShift?.id) {
      toast.error("No active shift found to close");
      return;
    }
    try {
      setIsClosingShift(true);
      const actual = parseFloat(shiftCloseCash) || 0;
      await closeShift(activeShift.id, actual, denominationCounts, activeShiftExpectedCash, activeShiftDiff, shiftCloseNote);

      toast.success("Shift closed and reconciled successfully!");
      setIsCloseShiftModalOpen(false);
      setShiftCloseCash('');
      setShiftCloseNote('');
      mutateShift();
      mutate();
      mutateClosedShifts();
    } catch (err) {
      console.error("Close shift error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to close shift");
    } finally {
      setIsClosingShift(false);
    }
  };

  return (
    <Page>
      <div className="max-w-[1700px] mx-auto p-4 sm:p-6 space-y-6">
        {/* TOP NAVIGATION TABS */}
        <div className="d-print-none">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                  <IconReceipt size={22} />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-black tracking-tight text-zinc-900 dark:text-white flex items-center gap-2">
                    DAY BOOK & FINANCIAL CONTROL
                    <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/70 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40 uppercase tracking-wide">
                      Audit & Reconciliation
                    </span>
                  </h1>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    Manage Shifts, Business Registers, Denomination Calculator & Financial Audit Records
                  </p>
                </div>
              </div>
            </div>

            {/* Segmented Tab Controls */}
            <div className="inline-flex p-1 rounded-2xl bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200/80 dark:border-zinc-700/80 shadow-inner gap-1 self-start md:self-auto overflow-x-auto max-w-full">
              <button 
                type="button"
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                  activeTab === 'daily' 
                    ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                }`}
                onClick={() => { setActiveTab('daily'); handlePeriodPreset('today'); }}
              >
                <IconCalendar size={15} /> Daily Summary
              </button>
              <button 
                type="button"
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                  activeTab === 'range' 
                    ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                }`}
                onClick={() => { setActiveTab('range'); setFilterType('range'); }}
              >
                <IconFilter size={15} /> Date Range
              </button>
              <button 
                type="button"
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                  activeTab === 'shifts' 
                    ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                }`}
                onClick={() => setActiveTab('shifts')}
              >
                <IconClock size={15} /> Shift Control
              </button>
              <button 
                type="button"
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                  activeTab === 'registers' 
                    ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                }`}
                onClick={() => setActiveTab('registers')}
              >
                <IconBuildingStore size={15} /> Register Control
              </button>
              <button 
                type="button"
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                  activeTab === 'transactions' 
                    ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                    : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                }`}
                onClick={() => setActiveTab('transactions')}
              >
                <IconReceipt size={15} /> Transaction Reports
              </button>
            </div>
          </div>
        </div>

        {/* FINANCIAL CONTROL STATUS RIBBON (Available across all views) */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-4 shadow-sm d-print-none">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Shift status */}
            <div className="flex items-center gap-3 flex-wrap">
              {activeShift ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800/50 text-emerald-800 dark:text-emerald-300 text-xs font-bold">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                    </span>
                    ACTIVE SHIFT #{activeShift.id}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
                    <span>Cashier: <strong className="text-zinc-900 dark:text-white">{activeShift.opened_by_name || activeShift.opened_by || activeShift.cashier_name || 'Cashier'}</strong></span>
                    <span className="text-zinc-300 dark:text-zinc-700">•</span>
                    <span>Opened: <strong className="text-zinc-900 dark:text-white">{formatDisplayTime(activeShift.opened_at)}</strong></span>
                    <span className="text-zinc-300 dark:text-zinc-700">•</span>
                    <span>Float: <strong className="text-emerald-600 dark:text-emerald-400 font-mono font-bold">{currency} {Number(activeShift.opening_balance || activeShift.opening_cash || 0).toFixed(2)}</strong></span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-300 text-xs font-bold">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500"></span>
                    NO ACTIVE SHIFT
                  </div>
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">
                    Start shift to record physical drawer float and track cashier sales.
                  </span>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {activeShift ? (
                <>
                  <button 
                    type="button"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 shadow-xs transition active:scale-95"
                    onClick={() => {
                      setShiftCloseCash(activeShiftExpectedCash.toFixed(2));
                      setIsCloseShiftModalOpen(true);
                    }}
                  >
                    <IconPlayerStop size={15} /> Close Shift
                  </button>
                  <button 
                    type="button"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/60 border border-emerald-200 dark:border-emerald-800/50 shadow-xs transition active:scale-95"
                    onClick={() => {
                      if (window.confirm(`Active Shift #${activeShift.id || activeShift.shift_number} is currently in progress.\n\nTo start a new shift, the current shift must be closed first.\n\nWould you like to close Shift #${activeShift.id || activeShift.shift_number} now?`)) {
                        setShiftCloseCash(activeShiftExpectedCash.toFixed(2));
                        setIsCloseShiftModalOpen(true);
                      }
                    }}
                    aria-label="Close active shift and start a new cashier session"
                  >
                    <IconPlayerPlay size={15} /> Start New Shift
                  </button>
                </>
              ) : (
                <button 
                  type="button"
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-xs transition active:scale-95"
                  onClick={() => {
                    setShiftStartFloat('');
                    setShiftStartNote('');
                    setIsStartShiftModalOpen(true);
                  }}
                >
                  <IconPlayerPlay size={15} /> Start Shift
                </button>
              )}

              <button 
                type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-zinc-700 dark:text-zinc-200 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 border border-zinc-200 dark:border-zinc-700 transition active:scale-95"
                onClick={() => openDenomDrawerFor('general')}
                aria-label="Open Multi-Currency Cash Denomination Calculator & Currency Converter"
              >
                <IconCalculator size={15} /> Denomination Calculator
              </button>

              <button 
                type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-zinc-700 dark:text-zinc-200 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 border border-zinc-200 dark:border-zinc-700 transition active:scale-95"
                onClick={() => {
                  setNewOpeningBalance(String(openingBalance));
                  setIsOpeningModalOpen(true);
                }}
                aria-label="Set Opening Cash Float for Day Book"
              >
                <IconCoins size={15} /> Set Drawer Float
              </button>

              {/* Professional Printable Reports Menu */}
              <div className="relative" ref={printDropdownRef}>
                <button 
                  type="button"
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-xs transition active:scale-95"
                  onClick={() => setIsPrintDropdownOpen(!isPrintDropdownOpen)}
                  aria-label="Generate and print professional reports with cashier profile verification"
                >
                  <IconPrinter size={15} /> Print Reports <IconChevronRight size={13} className={`transition-transform duration-150 ${isPrintDropdownOpen ? 'rotate-90' : ''}`} />
                </button>

                {isPrintDropdownOpen && (
                  <div 
                    className="absolute right-0 top-full mt-2 w-72 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl p-2 z-50 animate-in fade-in zoom-in-95 duration-100"
                  >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 dark:text-zinc-500 px-3 py-1">
                      Shift Audit Reports
                    </div>
                    <button 
                      type="button" 
                      className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800/80 transition text-left"
                      onClick={() => openPrintReport('shift_open', activeShift)}
                    >
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 flex items-center justify-center shrink-0">
                        <IconReceipt size={16} />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-zinc-900 dark:text-white">Shift Open Report</div>
                        <div className="text-[10px] text-zinc-400">Float & Multi-Currency Denominations</div>
                      </div>
                    </button>
                    <button 
                      type="button" 
                      className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800/80 transition text-left"
                      onClick={() => openPrintReport('shift_close', activeShift)}
                    >
                      <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 flex items-center justify-center shrink-0">
                        <IconReceipt size={16} />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-zinc-900 dark:text-white">Shift Close Z-Report</div>
                        <div className="text-[10px] text-zinc-400">Collections, Due, Expenses, Purchases</div>
                      </div>
                    </button>

                    <div className="border-t border-zinc-100 dark:border-zinc-800 my-1.5"></div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 dark:text-zinc-500 px-3 py-1">
                      Financial & Audit Reports
                    </div>
                    <button 
                      type="button" 
                      className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800/80 transition text-left"
                      onClick={() => openPrintReport('sales')}
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 flex items-center justify-center shrink-0">
                        <IconFileText size={16} />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-zinc-900 dark:text-white">Sales Audit Report</div>
                        <div className="text-[10px] text-zinc-400">Gross, Net, Tax, Bill Invoices</div>
                      </div>
                    </button>
                    <button 
                      type="button" 
                      className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800/80 transition text-left"
                      onClick={() => openPrintReport('purchase')}
                    >
                      <div className="w-8 h-8 rounded-lg bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300 flex items-center justify-center shrink-0">
                        <IconBuildingStore size={16} />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-zinc-900 dark:text-white">Purchase Audit Report</div>
                        <div className="text-[10px] text-zinc-400">Supplier Orders, Dues & Payments</div>
                      </div>
                    </button>
                    <button 
                      type="button" 
                      className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800/80 transition text-left"
                      onClick={() => openPrintReport('expenses')}
                    >
                      <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 flex items-center justify-center shrink-0">
                        <IconCash size={16} />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-zinc-900 dark:text-white">Expenses Audit Report</div>
                        <div className="text-[10px] text-zinc-400">Cash & Bank Expenditure Log</div>
                      </div>
                    </button>
                    <button 
                      type="button" 
                      className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-800/80 transition text-left"
                      onClick={() => openPrintReport('register')}
                    >
                      <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300 flex items-center justify-center shrink-0">
                        <IconBuildingStore size={16} />
                      </div>
                      <div>
                        <div className="text-xs font-bold text-zinc-900 dark:text-white">Register Audit Report</div>
                        <div className="text-[10px] text-zinc-400">Daily Master Drawer Audit</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              <button 
                type="button"
                className="w-8 h-8 rounded-xl flex items-center justify-center text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition active:scale-95"
                onClick={() => {
                  mutate();
                  mutateShift();
                  mutateClosedShifts();
                  mutateCurrentReg();
                  toast.success("Data refreshed!");
                }}
                aria-label="Refresh All Records"
              >
                <IconRefresh size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* TAB 1 & 2: DAY BOOK SUMMARY & DATE RANGE */}
        {(activeTab === 'daily' || activeTab === 'range') && (
          <>
            {/* DATE FILTER HEADER */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-4 shadow-sm d-print-none">
              <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                {/* Period presets and date selectors */}
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                    Report Period:
                  </span>
                  <div className="inline-flex p-1 rounded-xl bg-zinc-100 dark:bg-zinc-800 border border-zinc-200/80 dark:border-zinc-700/80 gap-1">
                    <button 
                      type="button"
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                        filterType === 'today' 
                          ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => handlePeriodPreset('today')}
                    >
                      Today
                    </button>
                    <button 
                      type="button"
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                        filterType === 'yesterday' 
                          ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => handlePeriodPreset('yesterday')}
                    >
                      Yesterday
                    </button>
                    <button 
                      type="button"
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                        filterType === 'custom' 
                          ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => handlePeriodPreset('custom')}
                    >
                      Custom Date
                    </button>
                    <button 
                      type="button"
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                        filterType === 'range' 
                          ? 'bg-white dark:bg-zinc-700 text-emerald-700 dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setFilterType('range')}
                    >
                      Date Range
                    </button>
                  </div>

                  {/* Date Pickers */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-400 font-medium">From:</span>
                    <input 
                      type="date" 
                      className="px-2.5 py-1.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-xs font-medium text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        if (filterType !== 'range') setToDate(e.target.value);
                      }}
                    />
                    {filterType === 'range' && (
                      <>
                        <span className="text-xs text-zinc-400 font-medium">To:</span>
                        <input 
                          type="date" 
                          className="px-2.5 py-1.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-xs font-medium text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </>
                    )}
                  </div>

                  {/* Previous / Next Day Steppers */}
                  {filterType !== 'range' && (
                    <div className="flex items-center gap-1">
                      <button 
                        type="button"
                        className="p-1.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-300 transition active:scale-95" 
                        onClick={() => handleShiftPeriod(-1)} 
                        aria-label="Previous Day"
                      >
                        <IconChevronLeft size={16} />
                      </button>
                      <button 
                        type="button"
                        className="p-1.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-300 transition active:scale-95" 
                        onClick={() => handleShiftPeriod(1)} 
                        aria-label="Next Day"
                      >
                        <IconChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Export and Print Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button 
                    type="button"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-zinc-700 dark:text-zinc-200 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 border border-zinc-200 dark:border-zinc-700 transition active:scale-95" 
                    onClick={handleExportCSV}
                  >
                    <IconFileSpreadsheet size={15} /> Export CSV
                  </button>
                  <button 
                    type="button" 
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/50 dark:hover:bg-amber-900/60 border border-amber-200 dark:border-amber-800/50 transition active:scale-95" 
                    onClick={() => openPrintReport('expenses')}
                    aria-label="Print Expenses Audit Report"
                  >
                    <IconCash size={15} /> Print Expenses
                  </button>
                  <button 
                    type="button" 
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-cyan-700 dark:text-cyan-300 bg-cyan-50 hover:bg-cyan-100 dark:bg-cyan-950/50 dark:hover:bg-cyan-900/60 border border-cyan-200 dark:border-cyan-800/50 transition active:scale-95" 
                    onClick={() => openPrintReport('purchase')}
                    aria-label="Print Purchase Audit Report"
                  >
                    <IconBuildingStore size={15} /> Print Purchases
                  </button>
                  <button 
                    type="button" 
                    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-xs transition active:scale-95" 
                    onClick={() => openPrintReport('sales')}
                    aria-label="Print Official Sales Audit Report"
                  >
                    <IconPrinter size={15} /> Print Sales Report
                  </button>
                </div>
              </div>
            </div>

            {/* MAIN REPORT CARD */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-5 sm:p-6 shadow-sm">
              {/* Store Printable Header */}
              <div className="text-center mb-6 pb-4 border-b border-zinc-100 dark:border-zinc-800">
                <h2 className="text-xl sm:text-2xl font-black text-zinc-900 dark:text-white tracking-tight">{storeName}</h2>
                <div className="text-xs text-zinc-500 mt-1">{storeAddress}</div>
                <div className="text-xs text-zinc-400 mt-0.5">PAN/VAT No: {storePan} | Phone: {storePhone}</div>
                <div className="mt-3">
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40">
                    Day Book Financial Report
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-1.5">
                  Audit Period: <strong className="text-zinc-800 dark:text-zinc-200">{formatDisplayDate(fromDate)}</strong> {fromDate !== toDate ? ` to ${formatDisplayDate(toDate)}` : ''}
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <div className="mt-3 text-xs text-zinc-400">Auditing financial transactions...</div>
                </div>
              ) : isError ? (
                <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/50 text-rose-700 dark:text-rose-300 text-xs text-center font-semibold">
                  Failed to load day book report. Please verify connection and retry.
                </div>
              ) : (
                <>
                  {/* EXECUTIVE KPI SUMMARY CARDS */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
                    {/* CARD 1: TOTAL BILLED SALES */}
                    <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 hover:border-emerald-500/40 transition-all shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold tracking-wider text-zinc-400 dark:text-zinc-500 uppercase">
                            Billed Sales
                          </span>
                          <div className="w-7 h-7 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                            <IconReceipt size={16} />
                          </div>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="text-2xl font-black text-zinc-900 dark:text-white font-mono tracking-tight truncate">
                            {currency} {totalBilled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <button 
                            type="button"
                            className="p-1.5 rounded-xl text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition active:scale-95"
                            aria-label="View All Billed Invoices "
                            onClick={() => openDrillDown('All Billed Invoices', 'billed')}
                          >
                            <IconEye size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-zinc-400 flex items-center justify-between mt-3 pt-2.5 border-t border-zinc-100 dark:border-zinc-800">
                        <span className="truncate">Gross: {currency} {grossSales.toFixed(2)}</span>
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">Audited</span>
                      </div>
                    </div>

                    {/* CARD 2: TOTAL COLLECTIONS */}
                    <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 hover:border-emerald-500/40 transition-all shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold tracking-wider text-zinc-400 dark:text-zinc-500 uppercase">
                            Collections
                          </span>
                          <div className="w-7 h-7 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                            <IconCoins size={16} />
                          </div>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 font-mono tracking-tight truncate">
                            {currency} {totalCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <button 
                            type="button"
                            className="p-1.5 rounded-xl text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition active:scale-95"
                            aria-label="View All Collections "
                            onClick={() => openDrillDown('All Collected Invoices', 'all')}
                          >
                            <IconEye size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-zinc-400 flex items-center justify-between mt-3 pt-2.5 border-t border-zinc-100 dark:border-zinc-800">
                        <span className="truncate">Cash: {currency} {totalCashCollected.toFixed(2)} | QR: {currency} {totalQrCollected.toFixed(2)}</span>
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">Received</span>
                      </div>
                    </div>

                    {/* CARD 3: SALES DUE (CREDIT CREATED) WITH PROMINENT EYE ICON */}
                    <div className="p-4 rounded-2xl bg-amber-50/40 dark:bg-amber-950/20 border-l-4 border-l-amber-500 border border-amber-200/60 dark:border-amber-800/40 shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold tracking-wider text-amber-800 dark:text-amber-300 uppercase">
                            Sales Due (Credit)
                          </span>
                          <span className="px-2 py-0.5 rounded-lg text-[10px] font-extrabold bg-amber-500 text-white shadow-xs">
                            {dueInvoicesCount} {dueInvoicesCount === 1 ? 'Invoice' : 'Invoices'}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="text-2xl font-black text-amber-600 dark:text-amber-400 font-mono tracking-tight truncate">
                            {currency} {salesDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <button 
                            type="button"
                            className="flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white shadow-xs transition active:scale-95"
                            aria-label="Click to view all Due Transactions for selected date "
                            onClick={() => openDrillDown('Due / Credit Sales Invoices', 'due')}
                          >
                            <IconEye size={14} />
                            <span>View Due</span>
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-zinc-400 flex items-center justify-between mt-3 pt-2.5 border-t border-amber-200/40 dark:border-amber-800/40">
                        <span className="text-zinc-500 dark:text-zinc-400">Receivables on this date</span>
                        <span className="text-amber-600 dark:text-amber-400 font-semibold">Audit Drilldown</span>
                      </div>
                    </div>

                    {/* CARD 4: ROOM CHARGES REVENUE */}
                    <div className="p-4 rounded-2xl bg-indigo-50/40 dark:bg-indigo-950/20 border-l-4 border-l-indigo-500 border border-indigo-200/60 dark:border-indigo-800/40 shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold tracking-wider text-indigo-800 dark:text-indigo-300 uppercase">
                            Room Revenue
                          </span>
                          <span className="px-2 py-0.5 rounded-lg text-[10px] font-extrabold bg-indigo-600 text-white shadow-xs">
                            {roomChargesCount} {roomChargesCount === 1 ? 'Stay' : 'Stays'}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="text-2xl font-black text-indigo-600 dark:text-indigo-400 font-mono tracking-tight truncate">
                            {currency} {totalRoomRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <button 
                            type="button"
                            className="flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs transition active:scale-95"
                            aria-label="Click to view all Room Stay charges for selected date "
                            onClick={() => openDrillDown('Room Charges Revenue', 'room_revenue')}
                          >
                            <IconEye size={14} />
                            <span>View Rooms</span>
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-zinc-400 flex items-center justify-between mt-3 pt-2.5 border-t border-indigo-200/40 dark:border-indigo-800/40">
                        <span className="text-zinc-500 dark:text-zinc-400">Accommodation Stays</span>
                        <span className="text-indigo-600 dark:text-indigo-400 font-semibold">Audit Drilldown</span>
                      </div>
                    </div>

                    {/* CARD 5: EXPECTED DRAWER CASH */}
                    <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 hover:border-cyan-500/40 transition-all shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold tracking-wider text-zinc-400 dark:text-zinc-500 uppercase">
                            Drawer Cash
                          </span>
                          <div className="w-7 h-7 rounded-xl bg-cyan-50 dark:bg-cyan-950/60 text-cyan-600 dark:text-cyan-400 flex items-center justify-center">
                            <IconCash size={16} />
                          </div>
                        </div>
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="text-2xl font-black text-zinc-900 dark:text-white font-mono tracking-tight truncate">
                            {currency} {expectedClosingCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <button 
                            type="button"
                            className="p-1.5 rounded-xl text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-950/60 hover:bg-cyan-100 dark:hover:bg-cyan-900/60 transition active:scale-95"
                            aria-label="View Cash Collections "
                            onClick={() => openDrillDown('Cash Collections', 'cash')}
                          >
                            <IconEye size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-zinc-400 flex items-center justify-between mt-3 pt-2.5 border-t border-zinc-100 dark:border-zinc-800">
                        <span className="truncate">Float: {currency} {openingBalance.toFixed(2)} | In: +{currency} {totalCashIn.toFixed(2)}</span>
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300">In-Hand</span>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 4: TWO-COLUMN FINANCIAL OVERVIEW */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* SALES SUMMARY */}
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-center mb-4 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                          <div>
                            <h4 className="font-extrabold text-sm uppercase tracking-wide text-zinc-800 dark:text-zinc-200 m-0">
                              Sales Summary
                            </h4>
                            <div className="text-[11px] text-zinc-400">Total invoiced sales performance and deductions</div>
                          </div>
                          <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40">
                            Audited Figures
                          </span>
                        </div>

                        <div className="border border-zinc-200/80 dark:border-zinc-800 rounded-xl overflow-hidden">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-zinc-50 dark:bg-zinc-800/70 text-zinc-500 dark:text-zinc-400 uppercase font-bold text-[10px] tracking-wider border-b border-zinc-200/80 dark:border-zinc-800">
                              <tr>
                                <th className="py-2.5 px-3.5">Item</th>
                                <th className="py-2.5 px-3.5 text-end">Amount</th>
                                <th className="py-2.5 px-3.5 text-center" style={{ width: '80px' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
                              <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                                <td className="py-2.5 px-3.5 font-medium text-zinc-700 dark:text-zinc-300">Gross Sales</td>
                                <td className="py-2.5 px-3.5 text-end font-bold font-mono text-zinc-900 dark:text-white">
                                  {currency} {grossSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View Underlying Invoices"
                                    onClick={() => openDrillDown('Gross Sales Invoices', 'gross')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                              <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                                <td className="py-2.5 px-3.5 font-medium text-rose-600 dark:text-rose-400">Discount</td>
                                <td className="py-2.5 px-3.5 text-end font-bold font-mono text-rose-600 dark:text-rose-400">
                                  -{currency} {discount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button 
                                      type="button"
                                      className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                      aria-label="View Discounted Sales"
                                      onClick={() => openDrillDown('Discounted Invoices', 'discount')}
                                    >
                                      <IconEye size={16} />
                                    </button>
                                    <button 
                                      type="button"
                                      className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition" 
                                      aria-label="Print Discount Report"
                                      onClick={() => window.print()}
                                    >
                                      <IconPrinter size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                                <td className="py-2.5 px-3.5 font-bold text-zinc-900 dark:text-white">Net Sales</td>
                                <td className="py-2.5 px-3.5 text-end font-black font-mono text-zinc-900 dark:text-white">
                                  {currency} {netSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View Net Sales Invoices"
                                    onClick={() => openDrillDown('Net Sales Invoices', 'net')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                              <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                                <td className="py-2.5 px-3.5 font-medium text-indigo-600 dark:text-indigo-400">VAT / Tax</td>
                                <td className="py-2.5 px-3.5 text-end font-bold font-mono text-indigo-600 dark:text-indigo-400">
                                  {currency} {tax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button 
                                      type="button"
                                      className="p-1 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-zinc-800 transition" 
                                      aria-label="View VAT Invoices"
                                      onClick={() => openDrillDown('Tax / VAT Invoices', 'tax')}
                                    >
                                      <IconEye size={16} />
                                    </button>
                                    <button 
                                      type="button"
                                      className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition" 
                                      aria-label="Print VAT Invoices"
                                      onClick={() => window.print()}
                                    >
                                      <IconPrinter size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                                <td className="py-2.5 px-3.5 font-medium text-zinc-500">Round Off</td>
                                <td className="py-2.5 px-3.5 text-end font-medium font-mono text-zinc-500">
                                  {roundOff < 0 ? '-' : ''}{currency} {Math.abs(roundOff).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button 
                                      type="button"
                                      className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                      aria-label="View Round Off Logs"
                                      onClick={() => openDrillDown('Round Off Invoices', 'round_off')}
                                    >
                                      <IconEye size={16} />
                                    </button>
                                    <button 
                                      type="button"
                                      className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition" 
                                      aria-label="Print"
                                      onClick={() => window.print()}
                                    >
                                      <IconPrinter size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                                <td className="py-2.5 px-3.5 font-medium text-zinc-500">Complementary Orders</td>
                                <td className="py-2.5 px-3.5 text-end font-mono text-zinc-500">
                                  {currency} {complementary.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View Complementary Invoices"
                                    onClick={() => openDrillDown('Complementary Orders', 'complementary')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                              <tr className="bg-emerald-50/70 dark:bg-emerald-950/40 border-t-2 border-emerald-500/40">
                                <td className="py-3 px-3.5 font-extrabold text-sm text-emerald-900 dark:text-emerald-200">
                                  Total Billed Sales
                                </td>
                                <td className="py-3 px-3.5 text-end font-black font-mono text-sm text-emerald-900 dark:text-emerald-200">
                                  {currency} {totalBilled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-3 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs transition active:scale-95" 
                                    aria-label="View All Invoices"
                                    onClick={() => openDrillDown('All Billed Invoices', 'billed')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                              <tr className="bg-amber-50/60 dark:bg-amber-950/30">
                                <td className="py-2.5 px-3.5 font-bold text-amber-800 dark:text-amber-300">
                                  <span className="inline-block px-1.5 py-0.2 mr-1 rounded bg-amber-500 text-white text-[10px] font-extrabold">CREDIT</span>
                                  Sales Due (Receivables Created)
                                </td>
                                <td className="py-2.5 px-3.5 text-end font-black font-mono text-amber-700 dark:text-amber-400">
                                  {currency} {salesDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-amber-600 hover:bg-amber-100 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View Sales Due Invoices"
                                    onClick={() => openDrillDown('Due / Credit Sales Invoices', 'due')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                              <tr className="bg-indigo-50/60 dark:bg-indigo-950/30">
                                <td className="py-2.5 px-3.5 font-bold text-indigo-800 dark:text-indigo-300">
                                  <span className="inline-block px-1.5 py-0.2 mr-1 rounded bg-indigo-600 text-white text-[10px] font-extrabold">ROOMS</span>
                                  Room Charges (Stay Revenue)
                                </td>
                                <td className="py-2.5 px-3.5 text-end font-black font-mono text-indigo-700 dark:text-indigo-400">
                                  {currency} {totalRoomRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-indigo-600 hover:bg-indigo-100 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View Room Stay Charges "
                                    onClick={() => openDrillDown('Room Charges Revenue', 'room_revenue')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* COLLECTION SUMMARY */}
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-center mb-4 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                          <div>
                            <h4 className="font-extrabold text-sm uppercase tracking-wide text-zinc-800 dark:text-zinc-200 m-0">
                              Collection Summary
                            </h4>
                            <div className="text-[11px] text-zinc-400">Realized collections by tender payment method</div>
                          </div>
                          <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40">
                            Payment Reconciled
                          </span>
                        </div>

                        {/* Quick KPI Highlights for Cash & QR */}
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div 
                            className="p-3 rounded-2xl bg-white dark:bg-zinc-900 border-l-4 border-l-[#70B56A] border border-zinc-200/80 dark:border-zinc-800 shadow-xs flex items-center justify-between cursor-pointer hover:shadow-sm transition active:scale-[0.99]"
                            onClick={() => openDrillDown('Cash Collections', 'cash')}
                            aria-label="Click to view Cash transactions"
                          >
                            <div>
                              <div className="text-[11px] text-zinc-400 font-bold uppercase tracking-wider">Total Cash</div>
                              <div className="font-black text-lg text-zinc-900 dark:text-white font-mono mt-0.5">
                                {currency} {totalCashCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </div>
                            <div className="p-1 rounded-lg text-emerald-600 bg-emerald-50 dark:bg-zinc-800">
                              <IconEye size={16} />
                            </div>
                          </div>
                          <div 
                            className="p-3 rounded-2xl bg-white dark:bg-zinc-900 border-l-4 border-l-emerald-500 border border-zinc-200/80 dark:border-zinc-800 shadow-xs flex items-center justify-between cursor-pointer hover:shadow-sm transition active:scale-[0.99]"
                            onClick={() => openDrillDown('QR & Digital Collections', 'qr')}
                            aria-label="Click to view QR / Digital transactions"
                          >
                            <div>
                              <div className="text-[11px] text-zinc-400 font-bold uppercase tracking-wider">Total QR / Digital</div>
                              <div className="font-black text-lg text-emerald-600 dark:text-emerald-400 font-mono mt-0.5">
                                {currency} {totalQrCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </div>
                            <div className="p-1 rounded-lg text-emerald-600 bg-emerald-50 dark:bg-zinc-800">
                              <IconEye size={16} />
                            </div>
                          </div>
                        </div>

                        <div className="border border-zinc-200/80 dark:border-zinc-800 rounded-xl overflow-hidden">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-zinc-50 dark:bg-zinc-800/70 text-zinc-500 dark:text-zinc-400 uppercase font-bold text-[10px] tracking-wider border-b border-zinc-200/80 dark:border-zinc-800">
                              <tr>
                                <th className="py-2.5 px-3.5">Payment Type</th>
                                <th className="py-2.5 px-3.5 text-end">Amount</th>
                                <th className="py-2.5 px-3.5 text-center" style={{ width: '80px' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
                              {salesPayments.map((p, idx) => (
                                <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                                  <td className="py-2.5 px-3.5 font-medium text-zinc-700 dark:text-zinc-300">{p.payment_method}</td>
                                  <td className="py-2.5 px-3.5 text-end font-bold font-mono text-zinc-900 dark:text-white">
                                    {currency} {Number(p.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-2.5 px-3.5 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <button 
                                        type="button"
                                        className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                        aria-label={`View ${p.payment_method} Transactions`}
                                        onClick={() => openDrillDown(`${p.payment_method} Collections`, `payment_${p.payment_method}`)}
                                      >
                                        <IconEye size={16} />
                                      </button>
                                      <button 
                                        type="button"
                                        className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition" 
                                        aria-label={`Print ${p.payment_method} Summary`}
                                        onClick={() => window.print()}
                                      >
                                        <IconPrinter size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              {salesPayments.length === 0 && (
                                <tr>
                                  <td colSpan={3} className="text-center text-zinc-400 py-4">No collections recorded in this period.</td>
                                </tr>
                              )}

                              {/* Dedicated Subtotal Rows for Cash & QR */}
                              <tr className="border-t-2 border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60">
                                <td className="py-2.5 px-3.5 font-bold text-zinc-900 dark:text-white">💵 Total Cash Collected</td>
                                <td className="py-2.5 px-3.5 text-end font-black font-mono text-zinc-900 dark:text-white">
                                  {currency} {totalCashCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View All Cash Collections"
                                    onClick={() => openDrillDown('Cash Collections', 'cash')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>

                              <tr className="bg-zinc-50 dark:bg-zinc-800/60">
                                <td className="py-2.5 px-3.5 font-bold text-emerald-600 dark:text-emerald-400">📱 Total QR / Digital Collected</td>
                                <td className="py-2.5 px-3.5 text-end font-black font-mono text-emerald-600 dark:text-emerald-400">
                                  {currency} {totalQrCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View All QR / Digital Collections"
                                    onClick={() => openDrillDown('QR & Digital Collections', 'qr')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>

                              <tr className="bg-emerald-50/70 dark:bg-emerald-950/40 border-t-2 border-emerald-500/40">
                                <td className="py-3 px-3.5 font-extrabold text-sm text-emerald-900 dark:text-emerald-200">
                                  Total Collected Receipts
                                </td>
                                <td className="py-3 px-3.5 text-end font-black font-mono text-sm text-emerald-900 dark:text-emerald-200">
                                  {currency} {totalCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-3 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs transition active:scale-95" 
                                    aria-label="View All Collections"
                                    onClick={() => openDrillDown('All Collected Invoices', 'all')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                              <tr className="bg-amber-50/60 dark:bg-amber-950/30">
                                <td className="py-2.5 px-3.5 font-bold text-amber-800 dark:text-amber-300">Sales Due (Receivables)</td>
                                <td className="py-2.5 px-3.5 text-end font-black font-mono text-amber-700 dark:text-amber-400">
                                  {currency} {salesDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="py-2.5 px-3.5 text-center">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-amber-600 hover:bg-amber-100 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View Due Invoices"
                                    onClick={() => openDrillDown('Due / Credit Invoices', 'due')}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 5: PHYSICAL CASH FLOW RECONCILIATION */}
                  <div className="border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-5 bg-zinc-50/60 dark:bg-zinc-800/40">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-zinc-200/80 dark:border-zinc-700/80">
                      <div>
                        <h4 className="font-extrabold text-sm uppercase tracking-wide text-zinc-800 dark:text-zinc-200 m-0">
                          Physical Cash Flow Reconciliation
                        </h4>
                        <div className="text-[11px] text-zinc-400">Audited Drawer Inflow, Outflow & Expected Physical Cash in Hand</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          type="button"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/60 border border-emerald-200 dark:border-emerald-800/50 transition active:scale-95"
                          onClick={() => openDenomDrawerFor('opening_balance')}
                        >
                          <IconCalculator size={15} /> Count Cash Drawer
                        </button>
                        <button 
                          type="button"
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-zinc-700 dark:text-zinc-200 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 border border-zinc-200 dark:border-zinc-700 transition active:scale-95"
                          onClick={() => {
                            setNewOpeningBalance(String(openingBalance));
                            setIsOpeningModalOpen(true);
                          }}
                        >
                          <IconCoins size={15} /> Edit Float
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* INFLOWS */}
                      <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-emerald-500/30 shadow-xs">
                        <div className="font-bold text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-3 pb-1 border-b border-emerald-100 dark:border-emerald-950/60 flex items-center justify-between">
                          <span>Cash Inflows (+)</span>
                          <span className="text-[10px] font-normal text-zinc-400">Receipts & Additions</span>
                        </div>
                        <table className="w-full text-xs text-left">
                          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
                            <tr>
                              <td className="py-2 text-zinc-500">Opening Cash Float:</td>
                              <td className="py-2 text-end font-bold font-mono text-zinc-900 dark:text-white">
                                {currency} {openingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-zinc-700 dark:text-zinc-300">• Cash Sales Collected</span>
                                  <button 
                                    type="button"
                                    className="p-1 rounded text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition"
                                    aria-label="View Cash Sales Invoices"
                                    onClick={() => openDrillDown('Cash Sales Invoices', 'cash')}
                                  >
                                    <IconEye size={15} />
                                  </button>
                                </div>
                              </td>
                              <td className="py-2 text-end font-bold font-mono text-emerald-600 dark:text-emerald-400">
                                +{currency} {cashSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-zinc-700 dark:text-zinc-300">• Previous Due Collected</span>
                                  <button 
                                    type="button"
                                    className="p-1 rounded text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition"
                                    aria-label="View Previous Due Collections"
                                    onClick={() => openDrillDown('Previous Due Collections', 'due_collected')}
                                  >
                                    <IconEye size={15} />
                                  </button>
                                </div>
                              </td>
                              <td className="py-2 text-end font-bold font-mono text-emerald-600 dark:text-emerald-400">
                                +{currency} {previousDueCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2 text-zinc-700 dark:text-zinc-300">• Other Cash Receipts / Transfers In</td>
                              <td className="py-2 text-end font-bold font-mono text-emerald-600 dark:text-emerald-400">
                                +{currency} {otherCashReceipts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            <tr className="bg-emerald-50 dark:bg-emerald-950/40 font-bold border-t border-emerald-200 dark:border-emerald-800/40">
                              <td className="py-2.5 px-2 text-emerald-900 dark:text-emerald-200">Total Cash Inflow (inc. Float)</td>
                              <td className="py-2.5 px-2 text-end font-black font-mono text-emerald-800 dark:text-emerald-300">
                                {currency} {(openingBalance + totalCashIn).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* OUTFLOWS */}
                      <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-rose-500/30 shadow-xs">
                        <div className="font-bold text-xs uppercase tracking-wider text-rose-700 dark:text-rose-400 mb-3 pb-1 border-b border-rose-100 dark:border-rose-950/60 flex items-center justify-between">
                          <span>Cash Outflows (-)</span>
                          <span className="text-[10px] font-normal text-zinc-400">Payments & Expenses</span>
                        </div>
                        <table className="w-full text-xs text-left">
                          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
                            <tr>
                              <td className="py-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-zinc-700 dark:text-zinc-300">• Cash Purchases Paid</span>
                                  <button 
                                    type="button"
                                    className="p-1 rounded text-rose-600 hover:bg-rose-50 dark:hover:bg-zinc-800 transition"
                                    aria-label="View Cash Purchases Vouchers"
                                    onClick={() => openDrillDown('Cash Purchases Outflows', 'cash_purchases')}
                                  >
                                    <IconEye size={15} />
                                  </button>
                                </div>
                              </td>
                              <td className="py-2 text-end font-bold font-mono text-rose-600 dark:text-rose-400">
                                -{currency} {cashPurchases.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-zinc-700 dark:text-zinc-300">• Cash Expenses Paid</span>
                                  <button 
                                    type="button"
                                    className="p-1 rounded text-rose-600 hover:bg-rose-50 dark:hover:bg-zinc-800 transition"
                                    aria-label="View Cash Expenses Vouchers"
                                    onClick={() => openDrillDown('Cash Expenses Outflows', 'cash_expenses')}
                                  >
                                    <IconEye size={15} />
                                  </button>
                                </div>
                              </td>
                              <td className="py-2 text-end font-bold font-mono text-rose-600 dark:text-rose-400">
                                -{currency} {cashExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2 text-zinc-700 dark:text-zinc-300">• Cash Refunds</td>
                              <td className="py-2 text-end font-bold font-mono text-rose-600 dark:text-rose-400">
                                -{currency} {cashRefunds.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            <tr>
                              <td className="py-2 text-zinc-700 dark:text-zinc-300">• Cash Withdrawals / Transfers Out</td>
                              <td className="py-2 text-end font-bold font-mono text-rose-600 dark:text-rose-400">
                                -{currency} {cashWithdrawals.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            <tr className="bg-rose-50 dark:bg-rose-950/40 font-bold border-t border-rose-200 dark:border-rose-800/40">
                              <td className="py-2.5 px-2 text-rose-900 dark:text-rose-200">Total Cash Outflow</td>
                              <td className="py-2.5 px-2 text-end font-black font-mono text-rose-800 dark:text-rose-300">
                                -{currency} {totalCashOut.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Expected Closing Cash Banner */}
                    <div className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 text-white rounded-2xl p-5 shadow-lg border border-zinc-700/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-5">
                      <div>
                        <div className="text-[11px] font-mono text-zinc-400 uppercase tracking-wider">
                          Formula: Opening Cash ({currency} {openingBalance.toFixed(2)}) + Cash In ({currency} {totalCashIn.toFixed(2)}) - Cash Out ({currency} {totalCashOut.toFixed(2)})
                        </div>
                        <div className="text-xl sm:text-2xl font-black text-white tracking-tight mt-0.5">
                          Expected Closing Cash
                        </div>
                      </div>
                      <div className="text-2xl sm:text-3xl font-black text-emerald-400 font-mono tracking-tight self-end sm:self-auto">
                        {currency} {expectedClosingCash.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* TAB 3: SHIFT CONTROL */}
        {activeTab === 'shifts' && (
          <div className="space-y-6">
            {/* Active Shift Card */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-5 sm:p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-zinc-100 dark:border-zinc-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                    <IconClock size={20} />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-black text-zinc-900 dark:text-white tracking-tight m-0">
                      Active Cashier Shift
                    </h3>
                    <div className="text-xs text-zinc-400">Current cashier shift session and live drawer reconciliation</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {activeShift ? (
                    <>
                      <button 
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-cyan-700 dark:text-cyan-300 bg-cyan-50 hover:bg-cyan-100 dark:bg-cyan-950/50 dark:hover:bg-cyan-900/60 border border-cyan-200 dark:border-cyan-800/50 transition active:scale-95"
                        onClick={() => openPrintReport('shift_open', activeShift)}
                        aria-label="Print Shift Open Report (Float & Denominations)"
                      >
                        <IconPrinter size={15} /> Shift Open Report
                      </button>
                      <button 
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:hover:bg-emerald-900/60 border border-emerald-200 dark:border-emerald-800/50 transition active:scale-95"
                        onClick={() => openPrintReport('shift_close', activeShift)}
                        aria-label="Print Live Shift Audit Report"
                      >
                        <IconPrinter size={15} /> Live Shift Report
                      </button>
                      <button 
                        type="button"
                        className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 shadow-xs transition active:scale-95"
                        onClick={() => {
                          setShiftCloseCash(activeShiftExpectedCash.toFixed(2));
                          setIsCloseShiftModalOpen(true);
                        }}
                      >
                        <IconPlayerStop size={15} /> End & Close Shift
                      </button>
                    </>
                  ) : (
                    <button 
                      type="button"
                      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-xs transition active:scale-95"
                      onClick={() => {
                        setShiftStartFloat('');
                        setShiftStartNote('');
                        setIsStartShiftModalOpen(true);
                      }}
                    >
                      <IconPlayerPlay size={15} /> Start New Shift
                    </button>
                  )}
                </div>
              </div>

              <div>
                {isLoadingShift ? (
                  <div className="text-center py-10">
                    <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <div className="mt-2 text-xs text-zinc-400">Loading active shift...</div>
                  </div>
                ) : activeShift ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Shift ID & Cashier</div>
                        <div className="text-xl font-black text-zinc-900 dark:text-white font-mono mt-1">#{activeShift.id || activeShift.shift_number}</div>
                        <div className="text-xs text-zinc-500 mt-0.5 truncate">{activeShift.opened_by_name || activeShift.opened_by || activeShift.cashier_name || 'Cashier'}</div>
                      </div>
                      <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Opening Drawer Float</div>
                        <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono mt-1">{currency} {activeShiftOpening.toFixed(2)}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Opened: {formatDisplayTime(activeShift.opened_at)}</div>
                      </div>
                      <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Cash Sales Collected</div>
                        <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono mt-1">+{currency} {activeShiftCashSales.toFixed(2)}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Paid in Cash</div>
                      </div>
                      <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Expected Drawer Cash</div>
                        <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 font-mono mt-1">{currency} {activeShiftExpectedCash.toFixed(2)}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Float + Cash Sales - Expenses</div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-bold text-sm text-zinc-900 dark:text-white m-0">Shift Transaction Activity</h4>
                      <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40">
                        {shiftSummary?.transactions?.length || 0} Transactions
                      </span>
                    </div>

                    <div className="border border-zinc-200/80 dark:border-zinc-800 rounded-2xl overflow-hidden">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-zinc-50 dark:bg-zinc-800/70 text-zinc-500 dark:text-zinc-400 uppercase font-bold text-[10px] tracking-wider border-b border-zinc-200/80 dark:border-zinc-800">
                          <tr>
                            <th className="py-2.5 px-3.5" style={{ width: '40px' }}>#</th>
                            <th className="py-2.5 px-3.5">Time</th>
                            <th className="py-2.5 px-3.5">Ref #</th>
                            <th className="py-2.5 px-3.5">Payment</th>
                            <th className="py-2.5 px-3.5">Customer</th>
                            <th className="py-2.5 px-3.5 text-end">Amount</th>
                            <th className="py-2.5 px-3.5 text-center" style={{ width: '90px' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
                          {(shiftSummary?.transactions || []).map((tx, idx) => (
                            <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                              <td className="py-2.5 px-3.5 text-zinc-400 font-mono">{idx + 1}</td>
                              <td className="py-2.5 px-3.5 text-zinc-500 text-[11px]">{formatDisplayTime(tx.time || tx.created_at)}</td>
                              <td className="py-2.5 px-3.5 font-bold text-zinc-900 dark:text-white">{tx.ref_no || tx.invoice_no || `INV-${tx.id}`}</td>
                              <td className="py-2.5 px-3.5">
                                <span className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                                  {tx.payment_method}
                                </span>
                              </td>
                              <td className="py-2.5 px-3.5 text-zinc-700 dark:text-zinc-300">{tx.customer_name || 'Walk-in'}</td>
                              <td className="py-2.5 px-3.5 text-end font-bold font-mono text-zinc-900 dark:text-white">{currency} {Number(tx.amount || 0).toFixed(2)}</td>
                              <td className="py-2.5 px-3.5 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                    aria-label="View Invoice Details"
                                    onClick={() => handleViewInvoice(tx)}
                                  >
                                    <IconEye size={16} />
                                  </button>
                                  <button 
                                    type="button"
                                    className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition" 
                                    aria-label="Print Thermal Receipt"
                                    onClick={() => handlePrintTransaction(tx)}
                                  >
                                    <IconPrinter size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {(!shiftSummary?.transactions || shiftSummary.transactions.length === 0) && (
                            <tr>
                              <td colSpan={7} className="text-center text-zinc-400 py-8 text-xs">No transactions recorded in this shift yet.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-3xl bg-zinc-100 dark:bg-zinc-800 text-zinc-400 flex items-center justify-center mx-auto mb-3">
                      <IconClock size={32} />
                    </div>
                    <h4 className="font-extrabold text-base text-zinc-900 dark:text-white">No Shift Currently Open</h4>
                    <p className="text-xs text-zinc-400 max-w-sm mx-auto mt-1 mb-4">
                      Open a new shift to track cash drawer balance, cashier accountability, and print closing Z-reports.
                    </p>
                    <button 
                      type="button"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 shadow-xs transition active:scale-95"
                      onClick={() => {
                        setShiftStartFloat('');
                        setShiftStartNote('');
                        setIsStartShiftModalOpen(true);
                      }}
                    >
                      <IconPlayerPlay size={15} /> Start Shift Now
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Closed Shifts History Card */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-5 sm:p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-zinc-100 dark:border-zinc-800">
                <div>
                  <h3 className="text-base font-black text-zinc-900 dark:text-white tracking-tight m-0">Closed Shifts History</h3>
                  <div className="text-xs text-zinc-400">Audited cashier shifts, opening floats, closing cash, and cash differences</div>
                </div>
                <button 
                  type="button"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold text-zinc-700 dark:text-zinc-200 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 transition active:scale-95" 
                  onClick={() => mutateClosedShifts()}
                >
                  <IconRefresh size={14} /> Refresh
                </button>
              </div>

              <div className="border border-zinc-200/80 dark:border-zinc-800 rounded-2xl overflow-hidden overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-zinc-50 dark:bg-zinc-800/70 text-zinc-500 dark:text-zinc-400 uppercase font-bold text-[10px] tracking-wider border-b border-zinc-200/80 dark:border-zinc-800">
                    <tr>
                      <th className="py-2.5 px-3">Shift #</th>
                      <th className="py-2.5 px-3">Cashier</th>
                      <th className="py-2.5 px-3">Date</th>
                      <th className="py-2.5 px-3">Opened</th>
                      <th className="py-2.5 px-3">Closed</th>
                      <th className="py-2.5 px-3 text-end">Float</th>
                      <th className="py-2.5 px-3 text-end">Expected</th>
                      <th className="py-2.5 px-3 text-end">Actual</th>
                      <th className="py-2.5 px-3 text-end">Difference</th>
                      <th className="py-2.5 px-3 text-center">Status</th>
                      <th className="py-2.5 px-3 text-center" style={{ width: '130px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
                    {(closedShifts || []).map((s, idx) => {
                      const diff = Number(s.cash_difference || 0);
                      return (
                        <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                          <td className="py-2.5 px-3 font-bold font-mono text-zinc-900 dark:text-white">#{s.id}</td>
                          <td className="py-2.5 px-3 font-medium text-zinc-800 dark:text-zinc-200">{s.cashier_name || 'Cashier'}</td>
                          <td className="py-2.5 px-3 text-zinc-500 text-[11px]">{s.business_date || '-'}</td>
                          <td className="py-2.5 px-3 text-zinc-400 text-[11px]">{s.opened_at ? formatDisplayDateTime(s.opened_at) : '-'}</td>
                          <td className="py-2.5 px-3 text-zinc-400 text-[11px]">{s.closed_at ? formatDisplayDateTime(s.closed_at) : '-'}</td>
                          <td className="py-2.5 px-3 text-end font-mono">{currency} {Number(s.opening_cash || 0).toFixed(2)}</td>
                          <td className="py-2.5 px-3 text-end font-mono">{currency} {Number(s.expected_cash || 0).toFixed(2)}</td>
                          <td className="py-2.5 px-3 text-end font-bold font-mono text-zinc-900 dark:text-white">{currency} {Number(s.closing_cash || 0).toFixed(2)}</td>
                          <td className="py-2.5 px-3 text-end font-mono">
                            {diff === 0 ? (
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">Balanced</span>
                            ) : diff > 0 ? (
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300">+{currency} {diff.toFixed(2)}</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">-{currency} {Math.abs(diff).toFixed(2)}</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                              {s.status}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button 
                                type="button"
                                className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                aria-label="Inspect Shift Details"
                                onClick={() => setSelectedShiftForDetails(s)}
                              >
                                <IconEye size={15} />
                              </button>
                              <button 
                                type="button"
                                className="px-2 py-0.5 rounded-lg text-[10px] font-bold text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-950/50 hover:bg-cyan-100 transition" 
                                aria-label="Print Shift Open Report"
                                onClick={() => openPrintReport('shift_open', s)}
                              >
                                Open
                              </button>
                              <button 
                                type="button"
                                className="px-2 py-0.5 rounded-lg text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 transition" 
                                aria-label="Print Shift Close Z-Report"
                                onClick={() => openPrintReport('shift_close', s)}
                              >
                                Close
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {(!closedShifts || closedShifts.length === 0) && (
                      <tr>
                        <td colSpan={11} className="text-center text-zinc-400 py-8 text-xs">
                          No closed shifts found in database.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: REGISTER CONTROL */}
        {activeTab === 'registers' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-4 shadow-sm flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                  Select Register Date:
                </span>
                <input 
                  type="date" 
                  className="px-2.5 py-1.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-xs font-medium text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20" 
                  value={selectedRegisterDate}
                  onChange={(e) => setSelectedRegisterDate(e.target.value)}
                />
              </div>
              <button 
                type="button"
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold text-zinc-700 dark:text-zinc-200 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 transition active:scale-95" 
                onClick={() => mutateByDateReg()}
              >
                <IconRefresh size={14} /> Refresh Register
              </button>
            </div>

            {/* Register Summary Card */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-5 sm:p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-zinc-100 dark:border-zinc-800">
                <div>
                  <h3 className="text-base font-black text-zinc-900 dark:text-white tracking-tight m-0">
                    Business Register — {formatDisplayDate(selectedRegisterDate)}
                  </h3>
                  <div className="text-xs text-zinc-400">Daily master cash drawer reconciliation and registered transactions</div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-xs transition active:scale-95"
                    onClick={() => openPrintReport('register', null, registerData)}
                    aria-label="Print Daily Register Audit Report"
                  >
                    <IconPrinter size={15} /> Print Register Report
                  </button>
                  <span className={`px-2.5 py-1 rounded-xl text-xs font-extrabold uppercase tracking-wider ${
                    registerData?.register?.status === 'open' 
                      ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40' 
                      : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
                  }`}>
                    Status: {registerData?.register?.status?.toUpperCase() || 'OPEN'}
                  </span>
                </div>
              </div>

              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Gross Sales</div>
                    <div className="text-xl font-black text-zinc-900 dark:text-white font-mono mt-1">{currency} {Number(registerData?.invoices?.total_gross || 0).toFixed(2)}</div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Discount Given</div>
                    <div className="text-xl font-black text-rose-600 dark:text-rose-400 font-mono mt-1">-{currency} {Number(registerData?.invoices?.total_discount || 0).toFixed(2)}</div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Total Tax (VAT)</div>
                    <div className="text-xl font-black text-indigo-600 dark:text-indigo-400 font-mono mt-1">+{currency} {Number(registerData?.invoices?.total_tax || 0).toFixed(2)}</div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/80 dark:border-zinc-700/80">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">Total Billed</div>
                    <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 font-mono mt-1">{currency} {Number(registerData?.invoices?.total_billed || 0).toFixed(2)}</div>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-bold text-sm text-zinc-900 dark:text-white m-0">Register Transactions</h4>
                  <span className="px-2.5 py-0.5 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40">
                    {registerData?.transactions?.length || 0} Transactions
                  </span>
                </div>

                <div className="border border-zinc-200/80 dark:border-zinc-800 rounded-2xl overflow-hidden">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-zinc-50 dark:bg-zinc-800/70 text-zinc-500 dark:text-zinc-400 uppercase font-bold text-[10px] tracking-wider border-b border-zinc-200/80 dark:border-zinc-800">
                      <tr>
                        <th className="py-2.5 px-3.5" style={{ width: '40px' }}>#</th>
                        <th className="py-2.5 px-3.5">Time</th>
                        <th className="py-2.5 px-3.5">Invoice / Ref #</th>
                        <th className="py-2.5 px-3.5">Customer</th>
                        <th className="py-2.5 px-3.5">Payment Method</th>
                        <th className="py-2.5 px-3.5 text-end">Amount</th>
                        <th className="py-2.5 px-3.5 text-center" style={{ width: '90px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
                      {(registerData?.transactions || []).map((t, idx) => (
                        <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                          <td className="py-2.5 px-3.5 text-zinc-400 font-mono">{idx + 1}</td>
                          <td className="py-2.5 px-3.5 text-zinc-500 text-[11px]">{formatDisplayTime(t.created_at || t.time)}</td>
                          <td className="py-2.5 px-3.5 font-bold text-zinc-900 dark:text-white">{t.invoice_no || `INV-${t.id}`}</td>
                          <td className="py-2.5 px-3.5 text-zinc-700 dark:text-zinc-300">{t.customer_name || 'Walk-in'}</td>
                          <td className="py-2.5 px-3.5">
                            <span className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                              {t.payment_method_name || 'Cash'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3.5 text-end font-bold font-mono text-zinc-900 dark:text-white">{currency} {Number(t.total || t.amount || 0).toFixed(2)}</td>
                          <td className="py-2.5 px-3.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button 
                                type="button"
                                className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition" 
                                aria-label="View Details"
                                onClick={() => handleViewInvoice(t)}
                              >
                                <IconEye size={16} />
                              </button>
                              <button 
                                type="button"
                                className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition" 
                                aria-label="Print Receipt"
                                onClick={() => handlePrintTransaction(t)}
                              >
                                <IconPrinter size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {(!registerData?.transactions || registerData.transactions.length === 0) && (
                        <tr>
                          <td colSpan={7} className="text-center text-zinc-400 py-8 text-xs">No transactions found for this business register.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: TRANSACTION REPORTS */}
        {activeTab === 'transactions' && (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800 rounded-2xl p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-zinc-100 dark:border-zinc-800">
              <div className="flex items-center gap-2.5">
                <button 
                  type="button"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold text-zinc-700 dark:text-zinc-200 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 transition active:scale-95"
                  onClick={() => setActiveTab('daily')}
                  aria-label="Return to Summary Report"
                >
                  <IconArrowLeft size={15} /> Back
                </button>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-zinc-900 dark:text-white tracking-tight m-0">
                    {txFilterTitle}
                  </h3>
                  <div className="text-xs text-zinc-400">
                    Period: {formatDisplayDate(fromDate)} {fromDate !== toDate ? ` — ${formatDisplayDate(toDate)}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 rounded-xl text-xs font-bold bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800/40">
                  {filteredTransactions.length} Records
                </span>
                <span className="px-3 py-1 rounded-xl text-xs font-black bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40 font-mono">
                  {currency} {filteredTransactionsTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <button 
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition active:scale-95 shadow-xs" 
                  onClick={() => window.print()}
                >
                  <IconPrinter size={15} /> Print List
                </button>
              </div>
            </div>

            {/* Quick Filter Switcher Pills */}
            <div className="flex flex-wrap gap-1.5 mb-4 pb-3 border-b border-zinc-100 dark:border-zinc-800">
              {[
                { key: 'all', label: 'All Invoices' },
                { key: 'gross', label: 'Gross Sales' },
                { key: 'net', label: 'Net Sales' },
                { key: 'discount', label: 'Discounted' },
                { key: 'tax', label: 'VAT / Tax' },
                { key: 'round_off', label: 'Round Off' },
                { key: 'complementary', label: 'Complementary' },
                { key: 'due', label: dueInvoicesCount > 0 ? `Sales Due (${dueInvoicesCount})` : 'Sales Due' },
                { key: 'due_collected', label: 'Previous Due Collected' },
                { key: 'room_revenue', label: roomChargesCount > 0 ? `Room Charges (${roomChargesCount})` : 'Room Charges' },
                { key: 'cash', label: 'Cash Only' },
                { key: 'card', label: 'Card' },
                { key: 'online', label: 'Online / QR' },
              ].map(pill => (
                <button
                  key={pill.key}
                  type="button"
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition active:scale-95 ${
                    txFilter === pill.key 
                      ? 'bg-emerald-600 text-white shadow-xs' 
                      : 'bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-400'
                  }`}
                  onClick={() => {
                    setTxFilter(pill.key);
                    setTxFilterTitle(pill.label === 'All Invoices' ? 'All Financial Transactions' : `${pill.label} Transactions`);
                  }}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            {/* Search Bar */}
            <div className="relative mb-4">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400">
                <IconSearch size={16} />
              </span>
              <input 
                type="text" 
                className="w-full pl-10 pr-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-xs text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition" 
                placeholder="Search invoice number, customer, payment method or item..." 
                value={txSearch}
                onChange={(e) => setTxSearch(e.target.value)}
              />
            </div>

            <div className="border border-zinc-200/80 dark:border-zinc-800 rounded-2xl overflow-hidden overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-zinc-50 dark:bg-zinc-800/70 text-zinc-500 dark:text-zinc-400 uppercase font-bold text-[10px] tracking-wider border-b border-zinc-200/80 dark:border-zinc-800">
                  <tr>
                    <th className="py-2.5 px-3" style={{ width: '40px' }}>#</th>
                    <th className="py-2.5 px-3">Invoice / Ref #</th>
                    <th className="py-2.5 px-3">Date / Time</th>
                    <th className="py-2.5 px-3">Customer</th>
                    <th className="py-2.5 px-3">Payment</th>
                    <th className="py-2.5 px-3 text-end">Subtotal</th>
                    <th className="py-2.5 px-3 text-end">Tax</th>
                    <th className="py-2.5 px-3 text-end">Discount</th>
                    <th className="py-2.5 px-3 text-end">Round Off</th>
                    <th className="py-2.5 px-3 text-end">Total</th>
                    <th className="py-2.5 px-3 text-end">Due</th>
                    <th className="py-2.5 px-3 text-center">Status</th>
                    <th className="py-2.5 px-3 text-center" style={{ width: '90px' }}>Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
                  {filteredTransactions.map((inv, idx) => (
                    <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
                      <td className="py-2.5 px-3 text-zinc-400 font-mono">{idx + 1}</td>
                      <td className="py-2.5 px-3 font-bold text-zinc-900 dark:text-white">
                        {inv.invoice_no || (inv.token_no ? `TOKEN-#${inv.token_no}` : `INV-${inv.id}`)}
                        {inv.token_no && (
                          <span className="ml-1 px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 font-mono text-[10px] font-bold">
                            #{inv.token_no}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-zinc-500 text-[11px]">
                        {inv.created_at || inv.date ? formatDisplayDateTime(inv.created_at || inv.date) : '-'}
                      </td>
                      <td className="py-2.5 px-3 text-zinc-700 dark:text-zinc-300">{inv.customer_name || 'Walk-in Customer'}</td>
                      <td className="py-2.5 px-3">
                        <span className="px-2 py-0.5 rounded-lg text-[10px] font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                          {inv.payment_method_name || 'Cash'}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-end font-mono text-zinc-600 dark:text-zinc-400">{currency} {Number(inv.sub_total || inv.total || 0).toFixed(2)}</td>
                      <td className="py-2.5 px-3 text-end font-mono text-zinc-600 dark:text-zinc-400">{currency} {Number(inv.tax_total || 0).toFixed(2)}</td>
                      <td className="py-2.5 px-3 text-end font-mono text-rose-600">
                        {Number(inv.discount || 0) > 0 ? `-${Number(inv.discount).toFixed(2)}` : '0.00'}
                      </td>
                      <td className="py-2.5 px-3 text-end font-mono text-zinc-400">
                        {currency} {Number(inv.round_off || 0).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-end font-bold font-mono text-zinc-900 dark:text-white">
                        {currency} {Number(inv.total || 0).toFixed(2)}
                      </td>
                      <td className="py-2.5 px-3 text-end font-bold font-mono text-amber-600">
                        {Number(inv.due_amount || 0) > 0 ? `${currency} ${Number(inv.due_amount).toFixed(2)}` : '-'}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                          inv.payment_status === 'paid' 
                            ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' 
                            : 'bg-amber-50 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                        }`}>
                          {inv.payment_status || (Number(inv.due_amount || 0) > 0 ? 'due' : 'completed')}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button 
                            type="button"
                            className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-zinc-800 transition"
                            aria-label="View Transaction Details"
                            onClick={() => handleViewInvoice(inv)}
                          >
                            <IconEye size={16} />
                          </button>
                          <button 
                            type="button"
                            className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                            aria-label="Print Thermal Receipt"
                            onClick={() => handlePrintTransaction(inv)}
                          >
                            <IconPrinter size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredTransactions.length === 0 && (
                    <tr>
                      <td colSpan={13} className="text-center text-zinc-400 py-10 text-xs">
                        No transactions found matching this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MODAL 0: DRILL-DOWN TRANSACTIONS MODAL (When clicking 👁 on Gross Sales, Net Sales, Tax, Discount, Cash, etc.) */}
        {drillModal.isOpen && (
          <ModalErrorBoundary onClose={() => setDrillModal(prev => ({ ...prev, isOpen: false }))}>
            <div 
              className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
              onClick={() => setDrillModal(prev => ({ ...prev, isOpen: false }))}
            >
              <div 
                className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-6xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                      <IconReceipt size={22} />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                        {drillModal.title}
                      </h3>
                      <p className="text-xs text-emerald-200/80 m-0 mt-0.5">
                        Period: {formatDisplayDate(fromDate)} {fromDate !== toDate ? ` — ${formatDisplayDate(toDate)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span className="px-3 py-1.5 rounded-xl text-xs font-bold bg-white/15 text-white border border-white/20">
                      {modalDrillItems.length} Records
                    </span>
                    <span className="px-3.5 py-1.5 rounded-xl text-xs font-black bg-[#70B56A] text-white shadow-sm">
                      {currency} {modalDrillTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <button 
                      type="button" 
                      className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95 ml-1"
                      onClick={() => setDrillModal(prev => ({ ...prev, isOpen: false }))}
                      aria-label="Close"
                    >
                      <IconX size={20} />
                    </button>
                  </div>
                </div>

                {/* Quick Search */}
                <div className="p-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/50 shrink-0">
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400">
                      <IconSearch size={18} />
                    </span>
                    <input 
                      type="text" 
                      className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#70B56A] transition" 
                      placeholder="Search invoice number, customer name, payment method..." 
                      value={drillSearch}
                      onChange={(e) => setDrillSearch(e.target.value)}
                      autoFocus
                    />
                    {drillSearch && (
                      <button 
                        type="button" 
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition"
                        onClick={() => setDrillSearch('')}
                      >
                        <IconX size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Table Body */}
                <div className="overflow-y-auto flex-1 p-0">
                  <table className="w-full text-left text-xs text-zinc-700 dark:text-zinc-300">
                    <thead className="sticky top-0 bg-zinc-100 dark:bg-zinc-800/90 backdrop-blur-xs text-zinc-600 dark:text-zinc-300 uppercase tracking-wider font-bold border-b border-zinc-200 dark:border-zinc-700 z-10">
                      <tr>
                        <th className="py-3 px-3.5 text-center" style={{ width: '40px' }}>#</th>
                        <th className="py-3 px-3">Invoice / Ref #</th>
                        <th className="py-3 px-3">Date & Time</th>
                        <th className="py-3 px-3">Customer</th>
                        <th className="py-3 px-3">Payment</th>
                        <th className="py-3 px-3 text-end">Subtotal</th>
                        <th className="py-3 px-3 text-end">Tax</th>
                        <th className="py-3 px-3 text-end">Discount</th>
                        <th className="py-3 px-3 text-end">Total</th>
                        <th className="py-3 px-3 text-end">Due</th>
                        <th className="py-3 px-3 text-center">Status</th>
                        <th className="py-3 px-3 text-center" style={{ width: '100px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                      {modalDrillItems.map((inv, idx) => (
                        <tr key={idx} className="hover:bg-emerald-50/40 dark:hover:bg-zinc-800/50 transition-colors">
                          <td className="py-3 px-3.5 text-center text-zinc-400">{idx + 1}</td>
                          <td className="py-3 px-3 font-bold text-zinc-900 dark:text-white">
                            {safeText(inv.invoice_no || (inv.token_no ? `TOKEN-#${inv.token_no}` : `INV-${inv.id}`))}
                            {inv.token_no && (
                              <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-[#243922] font-mono text-[10px] font-bold">
                                #{inv.token_no}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-zinc-500 text-[11px]">
                            {inv.created_at || inv.date ? formatDisplayDateTime(inv.created_at || inv.date) : '-'}
                          </td>
                          <td className="py-3 px-3 font-medium text-zinc-800 dark:text-zinc-200">
                            {safeText(inv.customer_name, 'Walk-in Customer')}
                          </td>
                          <td className="py-3 px-3">
                            <span className="px-2 py-0.5 rounded-lg text-[11px] font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
                              {safeText(inv.payment_method_name, 'Cash')}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-end text-zinc-600 dark:text-zinc-400">
                            {currency} {(Number(inv.sub_total || inv.total || 0) || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-end text-zinc-600 dark:text-zinc-400">
                            {currency} {(Number(inv.tax_total || 0) || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-end text-rose-600 font-medium">
                            {(Number(inv.discount || 0) || 0) > 0 ? `-${(Number(inv.discount) || 0).toFixed(2)}` : '0.00'}
                          </td>
                          <td className="py-3 px-3 text-end font-bold text-zinc-900 dark:text-white">
                            {currency} {(Number(inv.total || 0) || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-3 text-end font-bold text-amber-600">
                            {(Number(inv.due_amount || 0) || 0) > 0 ? `${currency} ${(Number(inv.due_amount) || 0).toFixed(2)}` : '-'}
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                              String(inv.payment_status || '').toLowerCase() === 'paid' 
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                            }`}>
                              {safeText(inv.payment_status, (Number(inv.due_amount || 0) > 0 ? 'due' : 'completed'))}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button 
                                type="button" 
                                className="p-1.5 rounded-xl text-[#243922] dark:text-emerald-300 bg-[#ECF1EB] dark:bg-emerald-950/60 hover:bg-[#DCE7DB] dark:hover:bg-emerald-900 border border-[#DCE7DB] dark:border-emerald-800/40 transition active:scale-95 shadow-xs"
                                aria-label="View Order Breakdown & Items "
                                onClick={() => handleViewInvoice(inv)}
                              >
                                <IconEye size={16} />
                              </button>
                              <button 
                                type="button" 
                                className="p-1.5 rounded-xl text-zinc-600 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition active:scale-95 shadow-xs"
                                aria-label="Print Thermal Receipt "
                                onClick={() => handlePrintTransaction(inv)}
                              >
                                <IconPrinter size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {modalDrillItems.length === 0 && (
                        <tr>
                          <td colSpan={12} className="text-center text-zinc-400 py-12 text-sm">
                            No transactions found matching your criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between flex-wrap gap-3 shrink-0">
                  <button 
                    type="button" 
                    className="px-4 py-2 rounded-xl text-xs font-bold text-[#243922] dark:text-emerald-400 bg-[#ECF1EB] dark:bg-zinc-800 hover:bg-[#DCE7DB] border border-[#DCE7DB] dark:border-zinc-700 transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => {
                      setDrillModal(prev => ({ ...prev, isOpen: false }));
                      setActiveTab('transactions');
                      setTxFilter(drillModal.type);
                      setTxFilterTitle(drillModal.title);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    <IconReceipt size={16} />
                    Open in Full Transaction Reports Tab
                  </button>
                  <div className="flex items-center gap-2">
                    <button 
                      type="button" 
                      className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                      onClick={() => openPrintReport('sales')}
                    >
                      <IconPrinter size={16} />
                      Print Sales Report
                    </button>
                    <button 
                      type="button" 
                      className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                      onClick={() => setDrillModal(prev => ({ ...prev, isOpen: false }))}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </ModalErrorBoundary>
        )}

        {/* MODAL 1: MULTI-CURRENCY DENOMINATIONS & CURRENCY CONVERTER DRAWER */}
        {isDenomDrawerOpen && (
          <div 
            className="fixed inset-0 z-[10000] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsDenomDrawerOpen(false)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-4xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconCalculator size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      Multi-Currency Cash Denomination Calculator
                    </h3>
                    <p className="text-xs text-emerald-200/80 m-0 mt-0.5">
                      Count physical cash notes & coins for drawer reconciliation
                    </p>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setIsDenomDrawerOpen(false)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                {/* Currency Selection Tabs */}
                <div className="flex items-center justify-between flex-wrap gap-3 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                  <div className="flex flex-wrap gap-1.5 p-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl">
                    {availableCurrencies.map(curr => (
                      <button
                        key={curr.code}
                        type="button"
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                          activeCurrency.code === curr.code
                            ? 'bg-[#70B56A] text-white shadow-sm'
                            : 'text-zinc-600 dark:text-zinc-300 hover:text-zinc-900'
                        }`}
                        onClick={() => setSelectedCurrencyCode(curr.code)}
                      >
                        <span>{curr.code} ({curr.symbol})</span>
                        {curr.is_base && <span className="text-[10px] bg-white/20 px-1 rounded">Base</span>}
                      </button>
                    ))}
                  </div>

                  {/* Live Exchange Rate / Converter Input */}
                  {!activeCurrency.is_base && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 text-xs">
                      <IconExchange size={15} className="text-zinc-400" />
                      <span className="text-zinc-500 font-medium">Exchange Rate to Base:</span>
                      <input 
                        type="number" 
                        step="any"
                        className="w-20 px-2 py-1 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-600 rounded-lg text-end font-bold text-zinc-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#70B56A]" 
                        value={activeExchangeRate}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 1.0;
                          setCustomRates(prev => ({ ...prev, [activeCurrency.code]: val }));
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Notes & Coins Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {(activeCurrency.denominations || []).map(d => {
                    const count = denominationCounts[activeCurrency.code]?.[d] ?? '';
                    const numCount = Number(count) || 0;
                    const lineTotal = numCount * Number(d);
                    return (
                      <div 
                        key={d}
                        className={`p-3 rounded-2xl border transition-all ${
                          numCount > 0 
                            ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 shadow-xs' 
                            : 'bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-[90px]">
                            <div className="text-lg font-black text-zinc-900 dark:text-white flex items-center gap-1">
                              <span>{activeCurrency.symbol}{d}</span>
                              <span className="text-[10px] font-semibold text-zinc-400 uppercase">{activeCurrency.code}</span>
                            </div>
                            <div className="text-[11px] text-zinc-500">
                              {numCount} × {d}
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            <button 
                              type="button" 
                              className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-200 transition active:scale-95"
                              onClick={() => handleIncrementDenomCount(activeCurrency.code, d, -1)}
                            >
                              -1
                            </button>
                            <input 
                              type="number" 
                              min="0"
                              className="w-16 h-7 text-center font-black text-sm bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-600 rounded-lg text-zinc-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#70B56A]"
                              value={count}
                              placeholder="0"
                              onChange={(e) => handleUpdateDenomCount(activeCurrency.code, d, e.target.value)}
                            />
                            <button 
                              type="button" 
                              className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-200 transition active:scale-95"
                              onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 1)}
                            >
                              +1
                            </button>
                            <button 
                              type="button" 
                              className="px-1.5 h-7 rounded-lg flex items-center justify-center font-bold text-xs bg-[#ECF1EB] dark:bg-emerald-950/60 hover:bg-[#DCE7DB] text-[#243922] dark:text-emerald-300 transition active:scale-95"
                              onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 5)}
                            >
                              +5
                            </button>
                            <button 
                              type="button" 
                              className="px-1.5 h-7 rounded-lg flex items-center justify-center font-bold text-xs bg-[#ECF1EB] dark:bg-emerald-950/60 hover:bg-[#DCE7DB] text-[#243922] dark:text-emerald-300 transition active:scale-95"
                              onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 10)}
                            >
                              +10
                            </button>
                          </div>

                          <div className="text-end min-w-[80px]">
                            <div className="text-xs font-black text-zinc-900 dark:text-white">
                              {activeCurrency.symbol} {lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary Footer Box */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-700 flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xs text-zinc-500">{activeCurrency.code} Subtotal:</div>
                    <div className="text-xl font-black text-zinc-900 dark:text-white">
                      {activeCurrency.symbol} {Number(denominationCalculations.perCurrency[activeCurrency.code]?.currTotal || 0).toFixed(2)}
                    </div>
                    {!activeCurrency.is_base && (
                      <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mt-0.5">
                        Converted Base Value: {currency} {Number(denominationCalculations.perCurrency[activeCurrency.code]?.baseTotal || 0).toFixed(2)}
                      </div>
                    )}
                  </div>

                  <div className="text-end">
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Grand Drawer Cash (Base Currency):</div>
                    <div className="text-2xl sm:text-3xl font-black text-[#70B56A]">
                      {currency} {denominationCalculations.grandBaseTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between flex-wrap gap-3 shrink-0">
                <button 
                  type="button" 
                  className="px-3.5 py-2 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 border border-rose-200 dark:border-rose-900 transition active:scale-95"
                  onClick={() => handleClearDenominations(activeCurrency.code)}
                >
                  Clear {activeCurrency.code} Counts
                </button>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                    onClick={() => setIsDenomDrawerOpen(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                    onClick={handleApplyDenominations}
                  >
                    <IconCheck size={16} />
                    {denomTarget === 'start_shift' ? 'Apply to Opening Float' : 
                     denomTarget === 'close_shift' ? 'Apply to Closing Cash' : 
                     denomTarget === 'opening_balance' ? 'Apply to Day Book Float' : 'Confirm Counted Cash'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 2: START SHIFT MODAL */}
        {isStartShiftModalOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsStartShiftModalOpen(false)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-2xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconPlayerPlay size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      Start Cashier Shift
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-emerald-200/90 flex-wrap">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 border border-white/20 font-semibold">
                        <IconUser size={13} /> {staffProfileName}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/10 font-semibold">
                        <IconCalendar size={13} /> {formatDisplayDate(fromDate)}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setIsStartShiftModalOpen(false)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handleStartShiftSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                  {/* Mode Switcher */}
                  <div className="flex p-1 bg-zinc-100 dark:bg-zinc-800 rounded-2xl">
                    <button
                      type="button"
                      className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                        startShiftTab === 'quick' 
                          ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setStartShiftTab('quick')}
                    >
                      <IconCash size={16} />
                      <span>⚡ Quick Float Amount</span>
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2.5 px-4 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                        startShiftTab === 'denominations' 
                          ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setStartShiftTab('denominations')}
                    >
                      <IconCalculator size={16} />
                      <span>🧮 Count Notes ({currency})</span>
                    </button>
                  </div>

                  {/* QUICK FLOAT MODE */}
                  {startShiftTab === 'quick' ? (
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-2">
                        Opening Cash Float ({currency})
                      </label>
                      <div className="relative flex items-center mb-3">
                        <span className="absolute left-4 font-bold text-lg text-zinc-500 dark:text-zinc-400">
                          {currency}
                        </span>
                        <input 
                          type="number" 
                          step="any"
                          required
                          className="w-full pl-16 pr-4 py-3.5 bg-zinc-50 dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl text-2xl font-black text-zinc-900 dark:text-white focus:outline-none focus:border-[#70B56A] focus:ring-4 focus:ring-[#70B56A]/15 transition"
                          placeholder="e.g. 5000"
                          value={shiftStartFloat}
                          onChange={(e) => setShiftStartFloat(e.target.value)}
                          autoFocus
                        />
                      </div>

                      {/* Quick Presets */}
                      <div>
                        <div className="text-xs font-semibold text-zinc-500 mb-2">Quick Presets / Add Amount:</div>
                        <div className="flex flex-wrap gap-2">
                          {[500, 1000, 2000, 5000, 10000, 20000].map(amt => (
                            <button
                              key={amt}
                              type="button"
                              className="px-3 py-1.5 rounded-xl text-xs font-bold bg-[#ECF1EB] hover:bg-[#DCE7DB] text-[#243922] border border-[#DCE7DB] transition active:scale-95"
                              onClick={() => {
                                const current = parseFloat(shiftStartFloat) || 0;
                                setShiftStartFloat(String(current + amt));
                              }}
                            >
                              +{currency} {amt.toLocaleString()}
                            </button>
                          ))}
                          <button
                            type="button"
                            className="px-3 py-1.5 rounded-xl text-xs font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition active:scale-95"
                            onClick={() => setShiftStartFloat('')}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* INTEGRATED DENOMINATIONS MODE */
                    <div className="space-y-4">
                      {/* Currency Tabs */}
                      <div className="flex items-center justify-between flex-wrap gap-2 pb-2 border-b border-zinc-100 dark:border-zinc-800">
                        <div className="flex flex-wrap gap-1 p-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl">
                          {availableCurrencies.map(curr => (
                            <button
                              key={curr.code}
                              type="button"
                              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition ${
                                activeCurrency.code === curr.code
                                  ? 'bg-[#70B56A] text-white shadow-sm'
                                  : 'text-zinc-600 dark:text-zinc-300'
                              }`}
                              onClick={() => setSelectedCurrencyCode(curr.code)}
                            >
                              {curr.code} ({curr.symbol}) {curr.is_base ? '★' : ''}
                            </button>
                          ))}
                        </div>
                        <div className="text-xs font-bold text-emerald-700 dark:text-emerald-300">
                          Counted Total: {currency} {denominationCalculations.grandBaseTotal.toFixed(2)}
                        </div>
                      </div>

                      {/* Denominations Steppers Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[220px] overflow-y-auto pr-1">
                        {(activeCurrency.denominations || []).map(d => {
                          const count = denominationCounts[activeCurrency.code]?.[d] ?? '';
                          const numCount = Number(count) || 0;
                          const lineTotal = numCount * Number(d);
                          return (
                            <div 
                              key={d}
                              className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 transition ${
                                numCount > 0 ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800' : 'bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700'
                              }`}
                            >
                              <div className="min-w-[70px]">
                                <span className="font-black text-sm text-zinc-900 dark:text-white">{activeCurrency.symbol}{d}</span>
                                <div className="text-[10px] text-zinc-500">{activeCurrency.code}</div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button 
                                  type="button" 
                                  className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, -1)}
                                >
                                  -
                                </button>
                                <input 
                                  type="number" 
                                  min="0"
                                  className="w-12 h-6 text-center font-black text-xs bg-white dark:bg-zinc-900 border border-zinc-300 rounded text-zinc-900 dark:text-white"
                                  value={count}
                                  placeholder="0"
                                  onChange={(e) => handleUpdateDenomCount(activeCurrency.code, d, e.target.value)}
                                />
                                <button 
                                  type="button" 
                                  className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 1)}
                                >
                                  +
                                </button>
                                <button 
                                  type="button" 
                                  className="px-1.5 h-6 rounded flex items-center justify-center font-bold text-[10px] bg-[#ECF1EB] text-[#243922] transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 5)}
                                >
                                  +5
                                </button>
                              </div>
                              <div className="text-end min-w-[65px]">
                                <span className="text-xs font-bold text-zinc-900 dark:text-white">{lineTotal.toFixed(0)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="p-3 rounded-2xl bg-[#ECF1EB] dark:bg-zinc-800 border border-[#DCE7DB] dark:border-zinc-700 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#243922] dark:text-emerald-300">Total Counted Float:</span>
                        <span className="text-lg font-black text-[#243922] dark:text-emerald-300">
                          {currency} {denominationCalculations.grandBaseTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Remarks Input */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-1.5">
                      Shift Notes / Float Remarks (Optional)
                    </label>
                    <textarea 
                      className="w-full px-3.5 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#70B56A]" 
                      rows={2} 
                      placeholder="e.g. Morning shift float assigned from main safe."
                      value={shiftStartNote}
                      onChange={(e) => setShiftStartNote(e.target.value)}
                    ></textarea>
                  </div>

                  {/* Security/Notice alert */}
                  <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 text-xs text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
                    <IconCheck size={16} className="text-emerald-600 shrink-0" />
                    <span>Drawer session will be opened for <strong>{staffProfileName}</strong> on <strong>{formatDisplayDate(fromDate)}</strong>.</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-end gap-2 shrink-0">
                  <button 
                    type="button" 
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                    onClick={() => setIsStartShiftModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-lg shadow-[#70B56A]/20 transition flex items-center gap-1.5 active:scale-95"
                    disabled={isStartingShift}
                  >
                    <IconPlayerPlay size={16} />
                    {isStartingShift ? 'Opening Shift...' : 'Confirm & Start Shift'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL 3: CLOSE SHIFT MODAL */}
        {isCloseShiftModalOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsCloseShiftModalOpen(false)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-2xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-rose-300 border border-white/10 shadow-inner">
                    <IconPlayerStop size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      End & Reconcile Shift #{activeShift?.id || activeShift?.shift_number}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-emerald-200/90 flex-wrap">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 border border-white/20 font-semibold">
                        <IconUser size={13} /> {activeShift?.cashier_name || activeShift?.opened_by_name || staffProfileName}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/10 font-semibold">
                        <IconClock size={13} /> Opened {formatDisplayTime(activeShift?.opened_at)}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setIsCloseShiftModalOpen(false)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handleCloseShiftSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4">
                  {/* Expected Drawer Cash Breakdown Card */}
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 space-y-2">
                    <div className="flex items-center justify-between text-xs text-zinc-600 dark:text-zinc-400">
                      <span>Opening Float:</span>
                      <span className="font-bold text-zinc-900 dark:text-white">{currency} {activeShiftOpening.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-emerald-600 font-medium">
                      <span>+ Cash Sales Collected:</span>
                      <span className="font-bold">+{currency} {activeShiftCashSales.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-rose-600 font-medium">
                      <span>- Cash Expenses Paid:</span>
                      <span className="font-bold">-{currency} {activeShiftCashExpenses.toFixed(2)}</span>
                    </div>
                    <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between">
                      <span className="text-xs font-black uppercase tracking-wider text-zinc-700 dark:text-zinc-300">
                        Expected Drawer Cash:
                      </span>
                      <span className="text-base font-black text-[#243922] dark:text-emerald-400">
                        {currency} {activeShiftExpectedCash.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Mode Switcher */}
                  <div className="flex p-1 bg-zinc-100 dark:bg-zinc-800 rounded-2xl">
                    <button
                      type="button"
                      className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                        closeShiftTab === 'quick' 
                          ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setCloseShiftTab('quick')}
                    >
                      <IconCash size={15} />
                      <span>⚡ Enter Actual Cash</span>
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 ${
                        closeShiftTab === 'denominations' 
                          ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                          : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                      onClick={() => setCloseShiftTab('denominations')}
                    >
                      <IconCalculator size={15} />
                      <span>🧮 Count Notes in Drawer</span>
                    </button>
                  </div>

                  {/* ACTUAL CASH INPUT MODE */}
                  {closeShiftTab === 'quick' ? (
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-2">
                        Actual Physical Cash In Hand ({currency})
                      </label>
                      <div className="relative flex items-center">
                        <span className="absolute left-4 font-bold text-lg text-zinc-500 dark:text-zinc-400">
                          {currency}
                        </span>
                        <input 
                          type="number" 
                          step="any"
                          required
                          className="w-full pl-16 pr-4 py-3.5 bg-zinc-50 dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl text-2xl font-black text-zinc-900 dark:text-white focus:outline-none focus:border-[#70B56A] focus:ring-4 focus:ring-[#70B56A]/15 transition"
                          placeholder="e.g. 15200"
                          value={shiftCloseCash}
                          onChange={(e) => setShiftCloseCash(e.target.value)}
                          autoFocus
                        />
                      </div>
                    </div>
                  ) : (
                    /* INTEGRATED DENOMINATIONS MODE */
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[190px] overflow-y-auto pr-1">
                        {(activeCurrency.denominations || []).map(d => {
                          const count = denominationCounts[activeCurrency.code]?.[d] ?? '';
                          const numCount = Number(count) || 0;
                          const lineTotal = numCount * Number(d);
                          return (
                            <div 
                              key={d}
                              className={`p-2 rounded-xl border flex items-center justify-between gap-2 transition ${
                                numCount > 0 ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800' : 'bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700'
                              }`}
                            >
                              <div className="min-w-[65px]">
                                <span className="font-black text-xs text-zinc-900 dark:text-white">{activeCurrency.symbol}{d}</span>
                                <div className="text-[10px] text-zinc-500">{activeCurrency.code}</div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button 
                                  type="button" 
                                  className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, -1)}
                                >
                                  -
                                </button>
                                <input 
                                  type="number" 
                                  min="0"
                                  className="w-11 h-6 text-center font-black text-xs bg-white dark:bg-zinc-900 border border-zinc-300 rounded text-zinc-900 dark:text-white"
                                  value={count}
                                  placeholder="0"
                                  onChange={(e) => handleUpdateDenomCount(activeCurrency.code, d, e.target.value)}
                                />
                                <button 
                                  type="button" 
                                  className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 1)}
                                >
                                  +
                                </button>
                                <button 
                                  type="button" 
                                  className="px-1.5 h-6 rounded flex items-center justify-center font-bold text-[10px] bg-[#ECF1EB] text-[#243922] transition"
                                  onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 5)}
                                >
                                  +5
                                </button>
                              </div>
                              <div className="text-end min-w-[55px]">
                                <span className="text-xs font-bold text-zinc-900 dark:text-white">{lineTotal.toFixed(0)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="p-2.5 rounded-xl bg-[#ECF1EB] dark:bg-zinc-800 border border-[#DCE7DB] dark:border-zinc-700 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#243922] dark:text-emerald-300">Total Counted Drawer Cash:</span>
                        <span className="text-base font-black text-[#243922] dark:text-emerald-300">
                          {currency} {denominationCalculations.grandBaseTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Live Reconciliation Difference Status */}
                  <div>
                    {activeShiftDiff === 0 ? (
                      <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 font-bold text-xs text-center flex items-center justify-center gap-2">
                        <IconCheck size={18} className="text-emerald-600" />
                        <span>✓ PERFECTLY BALANCED (Drawer cash matches expected balance)</span>
                      </div>
                    ) : activeShiftDiff > 0 ? (
                      <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 font-bold text-xs text-center flex items-center justify-center gap-2">
                        <IconAlertTriangle size={18} className="text-amber-600" />
                        <span>⚠ CASH OVERAGE: +{currency} {activeShiftDiff.toFixed(2)} (Actual exceeds expected)</span>
                      </div>
                    ) : (
                      <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-300 font-bold text-xs text-center flex items-center justify-center gap-2">
                        <IconAlertTriangle size={18} className="text-rose-600" />
                        <span>⚠ CASH SHORTAGE: -{currency} {Math.abs(activeShiftDiff).toFixed(2)} (Shortage in drawer)</span>
                      </div>
                    )}
                  </div>

                  {/* Closing Remarks */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-1.5">
                      Closing Remarks / Handover Notes
                    </label>
                    <textarea 
                      className="w-full px-3.5 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#70B56A]" 
                      rows={2} 
                      placeholder="Any comments regarding drawer closing or petty cash hand-over."
                      value={shiftCloseNote}
                      onChange={(e) => setShiftCloseNote(e.target.value)}
                    ></textarea>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-end gap-2 shrink-0">
                  <button 
                    type="button" 
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                    onClick={() => setIsCloseShiftModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 shadow-lg shadow-rose-600/20 transition flex items-center gap-1.5 active:scale-95"
                    disabled={isClosingShift}
                  >
                    <IconPlayerStop size={16} />
                    {isClosingShift ? 'Closing Shift...' : 'Confirm & Close Shift'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL 4: SHIFT DETAILS / Z-REPORT MODAL */}
        {selectedShiftForDetails && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setSelectedShiftForDetails(null)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-4xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                    <IconReceipt size={22} />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                      Shift Z-Report & Audit Summary #{selectedShiftForDetails.id}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-emerald-200/90 flex-wrap">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 border border-white/20 font-semibold">
                        <IconUser size={13} /> {selectedShiftForDetails.cashier_name || selectedShiftForDetails.opened_by_name || 'Cashier'}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/10 font-semibold">
                        <IconCalendar size={13} /> {selectedShiftForDetails.business_date || formatDisplayDate(fromDate)}
                      </span>
                      <span className={`px-2 py-0.5 rounded-lg font-bold text-[10px] uppercase ${
                        selectedShiftForDetails.closed_at 
                          ? 'bg-zinc-800 text-zinc-300 border border-zinc-700' 
                          : 'bg-emerald-500 text-white'
                      }`}>
                        {selectedShiftForDetails.closed_at ? 'Closed' : 'Active Session'}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setSelectedShiftForDetails(null)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                {/* 4 Stat Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Opening Float</div>
                    <div className="text-lg sm:text-xl font-black text-zinc-900 dark:text-white">
                      {currency} {Number(selectedShiftForDetails.opening_balance || selectedShiftForDetails.opening_cash || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Expected Cash</div>
                    <div className="text-lg sm:text-xl font-black text-[#243922] dark:text-emerald-400">
                      {currency} {Number(selectedShiftForDetails.system_expected_cash || selectedShiftForDetails.expected_cash || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs text-zinc-500 font-semibold mb-1">Actual Cash In Hand</div>
                    <div className="text-lg sm:text-xl font-black text-emerald-600">
                      {currency} {Number(selectedShiftForDetails.actual_cash_in_hand || selectedShiftForDetails.closing_cash || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                    <div className="text-xs font-semibold mb-1 text-zinc-500">Difference / Variance</div>
                    <div className={`text-lg sm:text-xl font-black ${
                      Number(selectedShiftForDetails.cash_difference || 0) === 0 
                        ? 'text-emerald-600' 
                        : Number(selectedShiftForDetails.cash_difference || 0) > 0 
                          ? 'text-amber-600' 
                          : 'text-rose-600'
                    }`}>
                      {Number(selectedShiftForDetails.cash_difference || 0) > 0 ? '+' : ''}
                      {currency} {Number(selectedShiftForDetails.cash_difference || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Timestamps Card */}
                <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Opened At:</span>
                    <span className="font-bold text-zinc-900 dark:text-white">
                      {selectedShiftForDetails.opened_at ? formatDisplayDateTime(selectedShiftForDetails.opened_at) : '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Closed At:</span>
                    <span className="font-bold text-zinc-900 dark:text-white">
                      {selectedShiftForDetails.closed_at ? formatDisplayDateTime(selectedShiftForDetails.closed_at) : 'Currently Active'}
                    </span>
                  </div>
                  {selectedShiftForDetails.closing_notes && (
                    <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700">
                      <span className="text-zinc-500 block mb-0.5">Closing Remarks:</span>
                      <span className="font-medium text-zinc-800 dark:text-zinc-200 italic">
                        "{selectedShiftForDetails.closing_notes}"
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between flex-wrap gap-2 shrink-0">
                <button 
                  type="button" 
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                  onClick={() => setSelectedShiftForDetails(null)}
                >
                  Close
                </button>
                <div className="flex items-center gap-2">
                  <button 
                    type="button" 
                    className="px-3.5 py-2 rounded-xl text-xs font-bold text-[#243922] dark:text-emerald-300 bg-[#ECF1EB] dark:bg-zinc-800 hover:bg-[#DCE7DB] border border-[#DCE7DB] dark:border-zinc-700 transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => openPrintReport('shift_open', selectedShiftForDetails)}
                  >
                    <IconPrinter size={15} />
                    Print Shift Open
                  </button>
                  <button 
                    type="button" 
                    className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => openPrintReport('shift_close', selectedShiftForDetails)}
                  >
                    <IconPrinter size={15} />
                    Print Shift Close (Z-Report)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 5: ITEMIZED INVOICE DETAILS MODAL (when clicking 👁 on any transaction) */}
        {viewingInvoice && (
          <ModalErrorBoundary onClose={() => setViewingInvoice(null)}>
            <div 
              className="fixed inset-0 z-[10000] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
              onClick={() => setViewingInvoice(null)}
            >
              <div 
                className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-3xl overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 sm:p-6 flex items-center justify-between shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10 shadow-inner">
                      <IconReceipt size={22} />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-black text-white m-0 tracking-tight flex items-center gap-2">
                        Invoice #{safeText(viewingInvoice.invoiceNo || viewingInvoice.invoice_no || viewingInvoice.id, 'N/A')}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-emerald-200/90 flex-wrap">
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/10 font-semibold">
                          <IconClock size={13} /> {viewingInvoice.created_at || viewingInvoice.date ? formatDisplayDateTime(viewingInvoice.created_at || viewingInvoice.date) : '-'}
                        </span>
                        {viewingInvoice.staff_name && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-white/15 border border-white/20 font-semibold">
                            <IconUser size={13} /> {safeText(viewingInvoice.staff_name)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                    onClick={() => setViewingInvoice(null)}
                  >
                    <IconX size={20} />
                  </button>
                </div>

                {/* Body */}
                <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5">
                  {/* Summary row */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                      <div className="text-xs text-zinc-500 font-semibold mb-1">Customer</div>
                      <div className="font-bold text-sm text-zinc-900 dark:text-white truncate">
                        {safeText(viewingInvoice.customer_name, 'Walk-in Customer')}
                      </div>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                      <div className="text-xs text-zinc-500 font-semibold mb-1">Room / Table</div>
                      <div className="font-bold text-sm text-zinc-900 dark:text-white truncate">
                        {safeText(viewingInvoice.table_title || viewingInvoice.room_name || (viewingInvoice.room_number ? `Room ${viewingInvoice.room_number}` : '') || (viewingInvoice.orders && viewingInvoice.orders[0]?.table_title), 'N/A')}
                      </div>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                      <div className="text-xs text-zinc-500 font-semibold mb-1">Payment Method</div>
                      <div className="font-bold text-sm text-zinc-900 dark:text-white">
                        {safeText(viewingInvoice.payment_method_name, 'Cash')}
                      </div>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700">
                      <div className="text-xs text-zinc-500 font-semibold mb-1">Status</div>
                      <span className={`inline-block px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                        String(viewingInvoice.payment_status || '').toLowerCase() === 'paid' 
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                      }`}>
                        {safeText(viewingInvoice.payment_status, 'completed')}
                      </span>
                    </div>
                    <div className="p-3.5 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 col-span-2 sm:col-span-1">
                      <div className="text-xs text-zinc-500 font-semibold mb-1">Invoice Total</div>
                      <div className="font-black text-sm text-[#243922] dark:text-emerald-400">
                        {currency} {(Number(viewingInvoice.total !== undefined ? viewingInvoice.total : (viewingInvoice.amount || 0)) || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Items list */}
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Itemized Breakdown</h4>
                    <div className="border border-zinc-200 dark:border-zinc-700 rounded-2xl overflow-hidden">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 font-bold border-b border-zinc-200 dark:border-zinc-700">
                          <tr>
                            <th className="py-2.5 px-3 text-center" style={{ width: '40px' }}>#</th>
                            <th className="py-2.5 px-3">Item</th>
                            <th className="py-2.5 px-3 text-center">Qty</th>
                            <th className="py-2.5 px-3 text-end">Price ({currency})</th>
                            <th className="py-2.5 px-3 text-end">Total ({currency})</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                          {(() => {
                            const ordersList = Array.isArray(viewingInvoice.orders) 
                              ? viewingInvoice.orders 
                              : (viewingInvoice.orders ? [viewingInvoice.orders] : []);
                            
                            let allItems = [];
                            for (const o of ordersList) {
                              if (!o) continue;
                              let items = o.items;
                              if (typeof items === 'string') {
                                try { items = JSON.parse(items); } catch(e) { items = []; }
                              }
                              if (Array.isArray(items)) {
                                allItems.push(...items);
                              }
                            }

                            if (allItems.length > 0) {
                              return allItems.map((it, idx) => {
                                if (!it) return null;
                                let addonsList = [];
                                if (Array.isArray(it.addons)) {
                                  addonsList = it.addons;
                                } else if (typeof it.addons === 'string' && it.addons.trim()) {
                                  try {
                                    const parsed = JSON.parse(it.addons);
                                    if (Array.isArray(parsed)) addonsList = parsed;
                                    else addonsList = [it.addons];
                                  } catch (e) {
                                    addonsList = it.addons.split(',').map(s => s.trim()).filter(Boolean);
                                  }
                                }

                                const qty = Number(it.quantity || 1) || 1;
                                const price = Number(it.price !== undefined ? it.price : (it.variant_price !== undefined ? it.variant_price : 0)) || 0;
                                const rowTotal = Number((price * qty) || 0) || 0;

                                return (
                                  <tr key={idx} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition">
                                    <td className="py-2.5 px-3 text-center text-zinc-400 font-mono text-[11px]">{idx + 1}</td>
                                    <td className="py-2.5 px-3">
                                      <div className="font-bold text-zinc-900 dark:text-white">
                                        {safeText(it.item_title || it.title, 'Menu Item')}
                                      </div>
                                      {it.variant_title && (
                                        <span className="inline-block mt-0.5 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40">
                                          Variant: {safeText(it.variant_title)}
                                        </span>
                                      )}
                                      {addonsList.length > 0 && (
                                        <div className="flex flex-wrap gap-1 items-center mt-1">
                                          <span className="text-[10px] font-semibold text-zinc-400">Addons:</span>
                                          {addonsList.map((a, aIdx) => {
                                            const aTitle = (typeof a === 'object' && a !== null) ? safeText(a.title || a.name || a.addon_title || `#${a.id || aIdx}`) : safeText(a);
                                            const aPrice = (typeof a === 'object' && a !== null && a.price) ? ` (+${currency} ${(Number(a.price) || 0).toFixed(2)})` : '';
                                            return (
                                              <span key={aIdx} className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
                                                {aTitle}{aPrice}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      )}
                                      {it.notes && (
                                        <div className="text-[10px] text-zinc-400 italic mt-0.5">
                                          Note: {safeText(it.notes)}
                                        </div>
                                      )}
                                    </td>
                                    <td className="py-2.5 px-3 text-center font-bold text-zinc-800 dark:text-zinc-200">
                                      {qty}
                                    </td>
                                    <td className="py-2.5 px-3 text-end text-zinc-500 font-mono">
                                      {price.toFixed(2)}
                                    </td>
                                    <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white font-mono">
                                      {rowTotal.toFixed(2)}
                                    </td>
                                  </tr>
                                );
                              });
                            }

                            if (viewingInvoice.item_name) {
                              const qty = Number(viewingInvoice.quantity || 1) || 1;
                              const price = Number(viewingInvoice.price || viewingInvoice.total || 0) || 0;
                              return (
                                <tr>
                                  <td className="py-2.5 px-3 text-center text-zinc-400 font-mono text-[11px]">1</td>
                                  <td className="py-2.5 px-3 font-bold text-zinc-900 dark:text-white">{safeText(viewingInvoice.item_name)}</td>
                                  <td className="py-2.5 px-3 text-center font-bold">{qty}</td>
                                  <td className="py-2.5 px-3 text-end text-zinc-500 font-mono">{price.toFixed(2)}</td>
                                  <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white font-mono">{(price * qty).toFixed(2)}</td>
                                </tr>
                              );
                            }

                            const summaryTotal = Number(viewingInvoice.subtotal !== undefined ? viewingInvoice.subtotal : (viewingInvoice.sub_total || viewingInvoice.total || 0)) || 0;
                            return (
                              <tr>
                                <td className="py-2.5 px-3 text-center text-zinc-400 font-mono text-[11px]">1</td>
                                <td className="py-2.5 px-3">
                                  <div className="font-bold text-zinc-900 dark:text-white">Order Items Breakdown</div>
                                  <div className="text-[11px] text-zinc-400">Consolidated bill total for Invoice #{safeText(viewingInvoice.invoiceNo || viewingInvoice.id, 'N/A')}</div>
                                </td>
                                <td className="py-2.5 px-3 text-center font-bold">1</td>
                                <td className="py-2.5 px-3 text-end text-zinc-500 font-mono">{summaryTotal.toFixed(2)}</td>
                                <td className="py-2.5 px-3 text-end font-bold text-zinc-900 dark:text-white font-mono">{summaryTotal.toFixed(2)}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Financial Breakdown card */}
                  <div className="flex justify-end">
                    <div className="w-full sm:w-80 p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-800/60 border border-zinc-200 dark:border-zinc-700 space-y-1.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Subtotal:</span>
                        <span className="font-bold text-zinc-900 dark:text-white">{currency} {(Number(viewingInvoice.subtotal !== undefined ? viewingInvoice.subtotal : (viewingInvoice.sub_total || 0)) || 0).toFixed(2)}</span>
                      </div>
                      {(Number(viewingInvoice.discount || 0) || 0) > 0 && (
                        <div className="flex justify-between text-rose-600 font-medium">
                          <span>Discount:</span>
                          <span>-{currency} {(Number(viewingInvoice.discount) || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {(Number(viewingInvoice.taxTotal || viewingInvoice.tax_total || 0) || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-zinc-500">VAT / Tax:</span>
                          <span className="font-bold text-zinc-900 dark:text-white">+{currency} {(Number(viewingInvoice.taxTotal || viewingInvoice.tax_total) || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {(Number(viewingInvoice.roundOff || viewingInvoice.round_off || 0) || 0) !== 0 && (
                        <div className="flex justify-between">
                          <span className="text-zinc-500">Round Off:</span>
                          <span className="font-bold text-zinc-900 dark:text-white">{currency} {(Number(viewingInvoice.roundOff || viewingInvoice.round_off) || 0).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700 flex justify-between text-sm font-black">
                        <span className="text-zinc-900 dark:text-white">Total Amount:</span>
                        <span className="text-[#243922] dark:text-emerald-400">{currency} {(Number(viewingInvoice.total || 0) || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-emerald-600 font-bold">
                        <span>Paid Amount:</span>
                        <span>{currency} {(Number(viewingInvoice.paidAmount !== undefined ? viewingInvoice.paidAmount : (viewingInvoice.paid_amount !== undefined ? viewingInvoice.paid_amount : viewingInvoice.total)) || 0).toFixed(2)}</span>
                      </div>
                      {(Number(viewingInvoice.dueAmount !== undefined ? viewingInvoice.dueAmount : (viewingInvoice.due_amount || 0)) || 0) > 0 && (
                        <div className="flex justify-between text-amber-600 font-bold">
                          <span>Due Amount:</span>
                          <span>{currency} {(Number(viewingInvoice.dueAmount !== undefined ? viewingInvoice.dueAmount : viewingInvoice.due_amount) || 0).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-between shrink-0">
                  <button 
                    type="button" 
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                    onClick={() => setViewingInvoice(null)}
                  >
                    Close
                  </button>
                  <button 
                    type="button" 
                    className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition flex items-center gap-1.5 active:scale-95"
                    onClick={() => handlePrintTransaction(viewingInvoice)}
                  >
                    <IconPrinter size={16} />
                    Print Thermal Receipt
                  </button>
                </div>
              </div>
            </div>
          </ModalErrorBoundary>
        )}

        {/* MODAL 6: SET OPENING BALANCE MODAL */}
        {isOpeningModalOpen && (
          <div 
            className="fixed inset-0 z-[9999] bg-black/65 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
            onClick={() => setIsOpeningModalOpen(false)}
          >
            <div 
              className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl border border-zinc-200 dark:border-zinc-800 w-full max-w-lg overflow-hidden my-auto flex flex-col max-h-[92vh] animate-in fade-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1e3a1d] via-[#243922] to-[#387F39] text-white p-5 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center text-emerald-300 border border-white/10">
                    <IconCoins size={20} />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-black text-white m-0 tracking-tight">
                      Set Day Book Opening Balance
                    </h3>
                    <p className="text-xs text-emerald-200/80 m-0 mt-0.5">
                      Business Date: {formatDisplayDate(fromDate)}
                    </p>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition active:scale-95"
                  onClick={() => setIsOpeningModalOpen(false)}
                >
                  <IconX size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1">
                {/* Mode Switcher */}
                <div className="flex p-1 bg-zinc-100 dark:bg-zinc-800 rounded-xl">
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                      openingModalTab === 'quick' 
                        ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                        : 'text-zinc-500 hover:text-zinc-900'
                    }`}
                    onClick={() => setOpeningModalTab('quick')}
                  >
                    <IconCash size={15} />
                    <span>⚡ Quick Amount</span>
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                      openingModalTab === 'denominations' 
                        ? 'bg-white dark:bg-zinc-700 text-[#243922] dark:text-emerald-400 shadow-sm' 
                        : 'text-zinc-500 hover:text-zinc-900'
                    }`}
                    onClick={() => setOpeningModalTab('denominations')}
                  >
                    <IconCalculator size={15} />
                    <span>🧮 Count Notes</span>
                  </button>
                </div>

                {openingModalTab === 'quick' ? (
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mb-2">
                      Opening Cash Amount ({currency})
                    </label>
                    <div className="relative flex items-center mb-3">
                      <span className="absolute left-4 font-bold text-base text-zinc-500">
                        {currency}
                      </span>
                      <input 
                        type="number" 
                        step="any"
                        className="w-full pl-14 pr-4 py-3 bg-zinc-50 dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl text-xl font-black text-zinc-900 dark:text-white focus:outline-none focus:border-[#70B56A] focus:ring-4 focus:ring-[#70B56A]/15 transition" 
                        value={newOpeningBalance}
                        onChange={(e) => setNewOpeningBalance(e.target.value)}
                        placeholder="e.g. 10000"
                        autoFocus
                      />
                    </div>

                    {/* Quick chips */}
                    <div className="flex flex-wrap gap-1.5">
                      {[1000, 2000, 5000, 10000, 20000, 50000].map(amt => (
                        <button
                          key={amt}
                          type="button"
                          className="px-2.5 py-1 rounded-lg text-xs font-bold bg-[#ECF1EB] hover:bg-[#DCE7DB] text-[#243922] border border-[#DCE7DB] transition active:scale-95"
                          onClick={() => {
                            const cur = parseFloat(newOpeningBalance) || 0;
                            setNewOpeningBalance(String(cur + amt));
                          }}
                        >
                          +{amt.toLocaleString()}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="px-2.5 py-1 rounded-lg text-xs font-bold bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition"
                        onClick={() => setNewOpeningBalance('')}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Integrated Denominations */
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-2 max-h-[190px] overflow-y-auto pr-1">
                      {(activeCurrency.denominations || []).map(d => {
                        const count = denominationCounts[activeCurrency.code]?.[d] ?? '';
                        const numCount = Number(count) || 0;
                        const lineTotal = numCount * Number(d);
                        return (
                          <div 
                            key={d}
                            className={`p-2 rounded-xl border flex items-center justify-between gap-2 transition ${
                              numCount > 0 ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800' : 'bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700'
                            }`}
                          >
                            <span className="font-black text-xs text-zinc-900 dark:text-white min-w-[60px]">{activeCurrency.symbol}{d}</span>
                            <div className="flex items-center gap-1">
                              <button 
                                type="button" 
                                className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                onClick={() => handleIncrementDenomCount(activeCurrency.code, d, -1)}
                              >
                                -
                              </button>
                              <input 
                                type="number" 
                                min="0"
                                className="w-12 h-6 text-center font-black text-xs bg-white dark:bg-zinc-900 border border-zinc-300 rounded text-zinc-900 dark:text-white"
                                value={count}
                                placeholder="0"
                                onChange={(e) => handleUpdateDenomCount(activeCurrency.code, d, e.target.value)}
                              />
                              <button 
                                type="button" 
                                className="w-6 h-6 rounded flex items-center justify-center font-bold text-xs bg-zinc-100 hover:bg-zinc-200 text-zinc-700 transition"
                                onClick={() => handleIncrementDenomCount(activeCurrency.code, d, 1)}
                              >
                                +
                              </button>
                            </div>
                            <span className="text-xs font-bold text-zinc-900 dark:text-white min-w-[50px] text-end">{lineTotal.toFixed(0)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="p-2.5 rounded-xl bg-[#ECF1EB] dark:bg-zinc-800 border border-[#DCE7DB] dark:border-zinc-700 flex items-center justify-between">
                      <span className="text-xs font-bold text-[#243922] dark:text-emerald-300">Total Counted:</span>
                      <span className="text-base font-black text-[#243922] dark:text-emerald-300">
                        {currency} {denominationCalculations.grandBaseTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}

                <div className="text-xs text-zinc-500">
                  This sets the drawer opening float for <strong>{formatDisplayDate(fromDate)}</strong>.
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/80 flex items-center justify-end gap-2 shrink-0">
                <button 
                  type="button" 
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-800 transition active:scale-95"
                  onClick={() => setIsOpeningModalOpen(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-[#70B56A] hover:bg-[#629c5d] shadow-sm transition active:scale-95"
                  disabled={isSavingOB}
                  onClick={handleSaveOpeningBalance}
                >
                  {isSavingOB ? 'Saving...' : 'Save Balance'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL 7: PROFESSIONAL PRINTABLE FINANCIAL REPORTS MODAL */}
        <FinancialReportsPrintModal
          isOpen={printModal.isOpen}
          onClose={() => setPrintModal(prev => ({ ...prev, isOpen: false }))}
          reportType={printModal.reportType}
          selectedShift={printModal.selectedShift || activeShift}
          selectedRegister={printModal.selectedRegister || registerData}
          dateRange={printModal.dateRange || { from: fromDate, to: toDate }}
          data={{
            ...data,
            sales_payments: salesPayments,
            total_cash_collected: totalCashCollected,
            total_qr_collected: totalQrCollected,
            total_card_collected: totalCardCollected,
            total_collected: totalCollected,
            transactions: filteredTransactions,
            detailedInvoices: data?.invoicesList || filteredTransactions,
            registerSummary: registerData,
            shift: printModal.selectedShift || activeShift,
            currentShift: activeShift
          }}
        />

      </div>
    </Page>
  );
}
